import { prefillDraft } from '../../core/patient-chat';
import { ConnectPatientPrompt, ConnectPatientPrompts } from '../../ui/connect-patient-prompts';
import { AfterViewChecked, Component, HostListener, createNgModule, ElementRef, EventEmitter, Injector, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ChatSheetService } from '../../chat-sheet.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Observable, Subscription, firstValueFrom, interval } from 'rxjs';
import { FeedbackService } from '../../../core/services/feedback.service';
import { ChatService, NetworkReferralPrompt } from '../../chat.service';
import { ChatContextEntries, ChatContextItem, ChatContextReceived, ChatContextResponse, ChatContextSent, NETWORK_SHARE_RETRY_MS, NetworkShareDecision, networkShareDecision, reviewStatusLabel, SHARE_MODE_LABELS, URGENCY_LABELS, visibleContextItems } from '../../../core/models/referral.model';
import { INBOX_RELOAD_EVENTS } from '../../../core/models/inbox-item.model';
import { SocketService } from '../../../core/services/socket.service';
import { ReferralService } from '../../../core/services/referral.service';
import { GroupService } from '../../../core/services/group.service';
// Types only (erased): the dialog itself is loaded with the patient module when it is first needed.
import type { PatientReferralDialogData, PatientReferralDialogResult } from '../../../patient/components/patient-file/patient-referral-dialog/patient-referral-dialog.component';
import { Thread } from '../../core/chat-store';
import { ActionCard, AttachmentView, composeFeatures, ComposeFeatures, ConversationReceipt, ConversationSummary, conversationAcceptsFiles, FilingView, ForwardedFrom, HubRequestError, LinkedFrom, LinkedTo, PinView, MessageTick, PatientMention, ThreadMessage } from '../../core/hub-types';
import { checkFile, FILE_ACCEPT, fileErrorText, MAX_FILES_PER_MESSAGE, prepareImage } from '../../core/file-upload';
import { Detection, detectPatients, normalise, PatientCandidate, searchPatients } from '../../core/patient-mentions';
import { activePeopleMentions, atQueryBefore, BodySegment, DraftPersonMention, MentionablePerson, mentionSegments, mentionText, peopleMentionSpans, searchPeople, withTrigger } from '../../core/people-mentions';
import { avatarHue, initialsOf, memberHues } from '../../core/avatar';
import { patientContextKey, showPatientContext } from '../../ui/patient-context';
import { copyContent, pastedFileName } from '../../ui/clipboard';
import { linkedFromLabel } from '../../ui/linked-from';
import { replyTargetSeq, scrollAfterRender, threadLoader } from '../../core/thread-scroll';
import { filingLabel, unlinkedLabel, unlinkQuestion } from '../../core/filing';
import { FilingMark, filingRuns, runFrom } from '../../ui/filing-runs';
import { UnlinkTarget, unlinkTargets } from '../../ui/unlink-targets';
import { Trigger, emptyPickerHint, triggerChips } from '../../ui/compose-triggers';
import { unlinkRunDone, unlinkRunQuestion } from '../../ui/unlink-run';
import { actionCardStatus, actionCardText, actionPath, actionPatients, ChatAction, actionsInDraft, messageTaskText, noteDraftFrom, rememberAction, searchActions, slashQueryBefore } from '../../core/actions';
import {
  ActionTile, actionCardPath, actionDraftText, actionPrefillExtras, actionSheetTiles, draftRun, loadRecentActions, localDay, messageActionPatients,
  saveRecentActions, withAction, withoutAssignees, bookingButtons, bookingPrefill, CardActions, cardActions, cardOutline, cardState, localClock,
} from '../../ui/chat-action-run';
import { newBelow, showJumpDown } from '../../ui/jump-down';
import { SUPPORT_NAME, supportSeen, supportTypists } from '../../ui/support-sender';
import { ForwardPickerComponent, ForwardPickerData } from '../forward-picker/forward-picker.component';
import { ConversationInfoDialogComponent } from '../conversation-info-dialog/conversation-info-dialog.component';
import { ImageDialogComponent } from '../../../shared/components/image-dialog/image-dialog.component';
import { isInlinePhoto, MessagePhotos, messagePhotos, photoViewerData } from './message-photos';
import { logReport } from '../../../core/diagnostics/log-capture';
import { environment } from '../../../../environments/environment';
import { Capacitor } from '@capacitor/core';

/**
 * One entry of the rendered thread: a day separator, a message (grouped under the one before when close), or, in a
 * referral chat, a referral card (the summary sent, the other side's official response) merged in by date.
 */
type Row = { kind: 'day'; key: string; label: string }
  | { kind: 'message'; key: string; message: ThreadMessage; grouped: boolean; context: boolean; filing: FilingMark | null }
  | { kind: 'record'; key: string; item: ChatContextItem }
  /** the end of a run of filed messages: "N messages filed to …" */
  | { kind: 'filed'; key: string; count: number; links: LinkedTo[] }
  /** the run still being filed, its line running on to the composer's filing bar */
  | { kind: 'filing-open'; key: string };

/** The Inbox's socket events that can change what a referral chat's cards say (a note arrived, one was reviewed...). */
const CONTEXT_EVENTS = [...new Set(['referral-note', 'referral-incoming', 'referral-practice-entry'].flatMap((t) => INBOX_RELOAD_EVENTS[t] || []))];
const CONTEXT_REFRESH_MS = 600;

/** A `#` mention in the draft: the patient, and the exact text it put there (a mention whose text is gone is dropped). */
interface DraftMention extends PatientMention {
  text: string;
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '✅', '🙏'];
const SUGGEST_AFTER_MS = 350;
/** A file URL from the hub lives 5 minutes; re-ask a little before. */
const FILE_URL_TTL_MS = 4 * 60_000;
const MAX_FILES = MAX_FILES_PER_MESSAGE;

/**
 * A file being sent with the next message: uploaded as soon as it is added (resumable, chat/core/file-upload.ts), with
 * progress; a failed one is retried from where it stopped; removing one that is uploading cancels it.
 */
interface DraftFile {
  key: string;
  name: string;
  state: 'preparing' | 'uploading' | 'ready' | 'failed';
  /** 0-100 while uploading */
  progress: number;
  /** what is uploaded (a large photo is shrunk first) */
  blob?: Blob;
  error?: string;
  view?: AttachmentView;
  cancel?: AbortController;
}
/** Shown once per browser before the first link (§8.3). */
const LINK_WARNING_KEY = 'chat.filingWarningSeen';
const GROUP_WITHIN_MS = 5 * 60_000;
const TYPING_REPEAT_MS = 3000;

const ERROR_TEXT: Record<string, string> = {
  READ_ONLY: 'This conversation is read-only.',
  FORBIDDEN: 'You cannot do that here.',
  RATE_LIMITED: 'Slow down a little and try again.',
  NOT_FOUND: 'This conversation is no longer available.',
  NOT_CONNECTED: 'Not connected to chat. Reconnecting…',
  BAD_REQUEST: 'That could not be sent.',
  UPLOAD_FAILED: 'The file did not upload. Try again: it continues where it stopped.',
  FILE_TYPE: fileErrorText('FILE_TYPE'),
  FILE_TOO_LARGE: fileErrorText('FILE_TOO_LARGE'),
  EXTERNAL_REFERRAL_NO_FILES: fileErrorText('EXTERNAL_REFERRAL_NO_FILES'),
  DOCUMENT_ALLOWANCE_USED: fileErrorText('DOCUMENT_ALLOWANCE_USED'),
  FILE_GRANT_REQUIRED: fileErrorText('FILE_GRANT_REQUIRED'),
  FILE_GRANT_INVALID: fileErrorText('FILE_GRANT_INVALID'),
  TIMEOUT: 'Chat did not answer in time.',
};

/**
 * One open conversation: its messages (the latest 50, more on scrolling up, O10), the composer, and the actions on a
 * message. Keeps the reader's place when older messages load above, and follows new messages only when the reader is
 * already at the bottom.
 */
@Component({
  selector: 'app-chat-thread',
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.scss'],
})
export class ChatThreadComponent implements OnChanges, OnDestroy, AfterViewChecked {
  @Input() conversation!: ConversationSummary;
  @Input() isOrgAdmin = false;
  /** Inside the floating window, whose header carries the title, back and details: this header is not drawn. */
  @Input() embedded = false;
  @Output() readonly back = new EventEmitter<void>();
  @ViewChild('scroller') scroller?: ElementRef<HTMLElement>;
  @ViewChild('composer') composer?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('linkInput') linkInput?: ElementRef<HTMLInputElement>;
  @ViewChild('filePicker') filePicker?: ElementRef<HTMLInputElement>;
  @ViewChild('letterDialog') letterDialog?: TemplateRef<unknown>;
  /** the referral whose full summary the letter dialog shows */
  letter: ChatContextSent | ChatContextReceived | null = null;

  // Files (M5): uploaded as soon as they are picked, sent with the next message.
  files: DraftFile[] = [];
  /** who has read the message whose tick was clicked (a group's "read by 3 of 4") */
  receipts: { read: ConversationReceipt[]; delivered: ConversationReceipt[]; waiting: ConversationReceipt[] } | null = null;
  @ViewChild('receiptsDialog') receiptsDialog?: TemplateRef<unknown>;
  @ViewChild('actionSheet') actionSheet?: TemplateRef<unknown>;
  /** for the file picker's accept attribute: what the hub accepts */
  readonly fileAccept = FILE_ACCEPT;
  /** a file is being dragged over the thread */
  dragging = false;
  /** short-lived hub URLs of image files shown in the thread, by attachment id */
  readonly fileUrls = new Map<string, { url: string; at: number }>();
  private readonly fileUrlLoading = new Set<string>();

  readonly quickReactions = QUICK_REACTIONS;
  /** the message whose action bar is open (right click or long press), if any */
  actionsFor: string | null = null;
  thread$!: Observable<{ thread: Thread | null; rows: Row[]; typists: string[]; me: string | null }>;
  draft = '';
  editing: { id: string; text: string } | null = null;
  replyTo: ThreadMessage | null = null;

  // Smart patient mentions (tasks/messaging-hub-architecture.md §8, M3).
  /** `#` mentions in the draft; each is linked when the message is sent */
  mentions: DraftMention[] = [];
  /** the `#` picker, while the caret is in a `#query` */
  picker: { start: number; end: number; results: PatientCandidate[]; active: number } | null = null;
  /** names found in the draft, offered as "Link to patient?" before sending */
  suggestions: Detection[] = [];
  /** names found in a sent message and not yet answered: the "did you mean" bubble only the sender sees */
  readonly askAfterSend = new Map<string, Detection[]>();
  /** linking an existing message: the message, and the patient search */
  linking: ThreadMessage | null = null;
  /** The notice at the top, shown in full after a tap: in a narrow window it is cut to two lines. */
  bannerExpanded = false;
  linkQuery = '';
  linkResults: PatientCandidate[] = [];
  private dismissed = new Set<string>();

  // @-mentions of people (2026-09-21): `@` offers who may be mentioned here (the hub's list); they are told.
  /** `@` mentions in the draft; their ids go with the message */
  personMentions: DraftPersonMention[] = [];
  /** the `@` picker, while the caret is in an `@query` */
  personPicker: { start: number; end: number; query: string; results: MentionablePerson[]; active: number } | null = null;

  // Chat actions (tasks/chat-actions.md): `/task` opens the patient's add-task page, filled in from the draft.
  /** the `/` picker, while the caret is in a `/query` */
  actionPicker: { start: number; end: number; query: string; results: ChatAction[]; active: number } | null = null;
  /** an action waiting for its patient from the `#` picker */
  private pendingAction: ChatAction | null = null;
  /** "Make a task" from a message waiting for its patient: the text and the @people it carries instead of the draft's */
  private pendingFrom: { text: string; principals: string[] } | null = null;
  /** the `/ Action` chip's sheet: every action, recently used first */
  actionTiles: ActionTile[] = [];
  /** task cards whose Done is on its way, by record id */
  readonly completing = new Set<string>();
  /** the message field has focus: the @ Colleague / # Patient chips show over it */
  composerFocused = false;
  /** body segments per message, so a thread re-rendered every second does not re-split every body */
  private readonly segmentCache = new Map<string, { key: string; segments: BodySegment[] }>();
  /**
   * Network chat (2026-09-22): what each patient linked here can do with the other side, per `<link>|<patient>`.
   * The chip under a message that stayed in this practice reads from it ("Share with Dr X", "not joined yet", or
   * "Not shared · Refer"). Filled once per patient when such a chip is first shown, and by every check.
   */
  private readonly shareStatus = new Map<string, NetworkShareDecision['kind'] | 'checking' | 'error'>();
  private destroyed = false;
  private suggestTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly drafts = new Map<string, { text: string; mentions: DraftMention[]; people: DraftPersonMention[] }>();
  /** log requests being answered now (by the request's message id), so a double tap sends once */
  readonly answeringLogs = new Set<string>();
  private lastTypingSent = 0;
  private stickToBottom = true;
  /** the round "down" button over the thread is showing */
  jumpDown = false;
  /** the latest message the reader has seen at the bottom, for the button's count */
  private seenSeq: number | null = null;
  private restore: { height: number; top: number } | null = null;
  /** colours of the people in this conversation (memberHues), by principal id */
  private hues = new Map<string, number>();
  private lastCount = 0;
  private sub = new Subscription();

  // Referral chats (phase 5, owner 2026-09-22): the summary sent and the official response, as cards among the messages.
  /** this conversation's referral cards, oldest first (GET referrals chat-context) */
  context: ChatContextItem[] = [];
  private contextTimer: ReturnType<typeof setTimeout> | null = null;
  readonly urgencyLabels = URGENCY_LABELS;
  readonly shareModeLabels = SHARE_MODE_LABELS;
  readonly reviewLabel = reviewStatusLabel;
  /** The conversation's pinned messages (at most three, newest first), and which one the pinned bar shows. */
  pins: PinView[] = [];
  pinIndex = 0;
  /** The patient chats this conversation is being filed to (a link starts it), shown as a banner over the composer. */
  filing: FilingView | null = null;
  /** The message the view was last scrolled to when opened at one, so it is not pulled back on every render. */
  private scrolledToSeq: number | null = null;

  constructor(
    readonly chat: ChatService,
    private readonly dialog: MatDialog,
    private readonly sheets: ChatSheetService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly snack: MatSnackBar,
    private readonly feedback: FeedbackService,
    private readonly router: Router,
    private readonly injector: Injector,
    private readonly referrals: ReferralService,
    private readonly groups: GroupService,
    private readonly sockets: SocketService,
  ) {
    // What the Inbox reloads on also changes a referral chat's cards: a note arrived, one was reviewed, a referral was
    // received or decided. Refetched for the open referral chat, a burst being one refetch.
    for (const event of CONTEXT_EVENTS) {
      this.sub.add(this.sockets.listen(event, 'chat-referral-cards').subscribe(() => this.scheduleContextRefresh()));
    }
  }

  /** Someone pinned or unpinned in this conversation: the hub says so, and the bar follows. */
  private readonly filingWatch = this.chat.store.filingChanged$.subscribe((id) => {
    if (id === this.conversation?.id) void this.loadFiling();
  });
  private readonly pinsWatch = this.chat.store.pinsChanged$.subscribe((id) => {
    if (id === this.conversation?.id) void this.loadPins();
  });
  /** A money card's Chat button opened the conversation this thread already shows. */
  private readonly prefillWatch = this.chat.store.prefilled$.subscribe((id) => {
    if (id === this.conversation?.id) this.applyPrefill();
  });

  ngOnChanges(changes: SimpleChanges): void {
    const prev = changes['conversation']?.previousValue as ConversationSummary | undefined;
    if (prev && prev.id === this.conversation.id) return;
    if (prev) {
      this.chat.store.close(prev.id);
      this.drafts.set(prev.id, { text: this.draft, mentions: this.mentions, people: this.personMentions });
      // Also on the device, so what was typed survives a reload or a closed window (chat/core/chat-cache.ts).
      this.chat.store.saveDraft(prev.id, this.draft);
      if (this.lastTypingSent) this.chat.store.typing(prev.id, false);
    }
    const saved = this.drafts.get(this.conversation.id);
    this.draft = saved?.text ?? this.chat.store.draftOf(this.conversation.id);
    this.mentions = saved?.mentions ?? [];
    this.personMentions = saved?.people ?? [];
    this.personPicker = null;
    this.actionPicker = null;
    this.pendingAction = null;
    this.pendingFrom = null;
    this.segmentCache.clear();
    this.editing = null;
    this.replyTo = null;
    for (const f of this.files) f.cancel?.abort();
    this.files = [];
    this.picker = null;
    this.suggestions = [];
    this.dismissed = new Set();
    this.linking = null;
    this.applyPrefill();
    // Warm the colleague list, so "Dr <colleague>" is not offered as a patient.
    void this.chat.mentionContext(this.conversation.orgId);
    // Each member's colour in this conversation, so no two people here share one.
    this.hues = new Map();
    const forConversation = this.conversation.id;
    this.chat.store.members(forConversation)
      .then((members) => { if (this.conversation.id === forConversation) this.hues = memberHues(members.map((m) => m.principalId)); })
      .catch(() => undefined);
    // ...and who may be @-mentioned here, so the picker and the highlighted names are there at once.
    void this.chat.store.mentionable(this.conversation.id).catch(() => undefined);
    this.stickToBottom = true;
    this.jumpDown = false;
    this.seenSeq = null;
    this.lastCount = 0;
    const id = this.conversation.id;
    // Re-rendered every second too, so "typing…" expires on its own.
    this.thread$ = new Observable((subscriber) => {
      const emit = () => {
        const s = this.chat.store.snapshot;
        const me = s.me?.principalId ?? null;
        const stored = s.threads[id] ?? null;
        // The person who asked support sees the answers as MedicAI Support, not the agent who sent them.
        const asker = this.supportAsker();
        const thread = stored && asker ? { ...stored, messages: supportSeen(stored.messages, me) } : stored;
        const typists = this.chat.store.typists(id);
        subscriber.next({ thread, rows: this.rows(thread?.messages ?? [], !!thread?.hasMore), typists: asker ? supportTypists(typists) : typists, me });
      };
      const a = this.chat.store.state$.subscribe(emit);
      const b = interval(1000).subscribe(emit);
      return () => {
        a.unsubscribe();
        b.unsubscribe();
      };
    });
    this.chat.store.open(id).catch((e: unknown) => this.fail(e));
    this.resumeReferredLinks();
    this.context = [];
    void this.loadContext();
    this.pins = [];
    this.pinIndex = 0;
    void this.loadPins();
    this.filing = null;
    void this.loadFiling();
    this.scrolledToSeq = null;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.suggestTimer) clearTimeout(this.suggestTimer);
    if (this.contextTimer) clearTimeout(this.contextTimer);
    if (this.lastTypingSent) this.chat.store.typing(this.conversation.id, false);
    // Not being looked at any more (back to the list, minimised, closed): new messages count as unread again.
    this.chat.store.close(this.conversation.id);
    this.sub.unsubscribe();
    this.pinsWatch.unsubscribe();
    this.filingWatch.unsubscribe();
    this.prefillWatch.unsubscribe();
  }

  ngAfterViewChecked(): void {
    const el = this.scroller?.nativeElement;
    if (!el) return;
    const count = el.querySelectorAll('.msg').length;
    // Opened AT a message (a pin, "Go to it", a search hit): bring it to the middle of the view, once it is drawn.
    const target = this.pinnedSeq();
    if (target == null) this.scrolledToSeq = null;
    else if (target !== this.scrolledToSeq) {
      const found = el.querySelector<HTMLElement>(`.msg[data-seq="${target}"]`);
      if (found) {
        this.scrolledToSeq = target;
        this.stickToBottom = false;
        this.lastCount = count;
        // Only the messages move (scrollIntoView would also scroll the page behind the chat window); no animation
        // in a hidden tab, where browsers do not run it.
        const top = found.offsetTop - el.offsetTop - (el.clientHeight - found.offsetHeight) / 2;
        el.scrollTo({ top: Math.max(0, top), behavior: document.visibilityState === 'visible' ? 'smooth' : 'auto' });
        return;
      }
    }
    const next = scrollAfterRender({ count, lastCount: this.lastCount, stickToBottom: this.stickToBottom, scrollHeight: el.scrollHeight, restore: this.restore,
      loading: !!this.chat.store.snapshot.threads[this.conversation.id]?.loading });
    if (next.scrollTop !== null) el.scrollTop = next.scrollTop;
    if (!next.keepRestore) this.restore = null;
    this.lastCount = count;
    if (this.stickToBottom && !this.isHistoric()) this.seenSeq = this.latestSeq();
  }

  private latestSeq(): number | null {
    const messages = this.chat.store.snapshot.threads[this.conversation.id]?.messages ?? [];
    return messages.length ? messages[messages.length - 1].seq : null;
  }

  // ── rendering ────────────────────────────────────────────────────────────────────

  private rows(messages: ThreadMessage[], hasMore = false): Row[] {
    this.loadImageUrls(messages);
    const out: Row[] = [];
    let day = '';
    let prev: ThreadMessage | null = null;
    let newDay = false;
    const messageRows: { key: string; newDay: boolean }[] = [];
    const dayOf = (d: Date) => {
      const key = d.toDateString();
      if (key === day) return;
      out.push({ kind: 'day', key: `day-${key}`, label: this.dayLabel(d) });
      day = key;
      prev = null;
      newDay = true;
    };
    // Referral cards go in by date, as the referrals dashboard's stream puts them: a card before a message at the same
    // moment, and none above the oldest loaded message while older ones are still to come.
    const records = visibleContextItems(this.context, messages[0]?.createdAt ?? null, hasMore);
    let r = 0;
    const recordsUntil = (t: number) => {
      while (r < records.length && new Date(records[r].at).getTime() <= t) {
        dayOf(new Date(records[r].at));
        out.push({ kind: 'record', key: `rec-${records[r].kind}-${records[r].id}`, item: records[r] });
        prev = null;
        r++;
      }
    };
    for (const m of messages) {
      const d = new Date(m.createdAt);
      recordsUntil(d.getTime());
      dayOf(d);
      const grouped = !!prev && prev.kind === 'user' && m.kind === 'user' && prev.sender?.principalId === m.sender?.principalId
        && d.getTime() - new Date(prev.createdAt).getTime() < GROUP_WITHIN_MS;
      out.push({ kind: 'message', key: m.id, message: m, grouped, context: false, filing: null });
      messageRows.push({ key: m.kind === 'system' ? '' : patientContextKey(this.visibleLinks(m)), newDay });
      newDay = false;
      prev = m;
    }
    recordsUntil(Number.POSITIVE_INFINITY);
    // The patient banner: where the patients being talked about change (every time in a network chat, for Share).
    const network = this.conversation.type === 'network';
    const banners = showPatientContext(messageRows, network);
    // The filing line. Not in a network chat, where every message carries its own banner and Share.
    const runs = network ? null : filingRuns(messageRows.map((r, k) => ({ ...r, banner: banners[k] })), patientContextKey(this.filing?.patients ?? []));
    const withLine: Row[] = [];
    let i = 0;
    for (const row of out) {
      withLine.push(row);
      if (row.kind !== 'message') continue;
      row.context = banners[i];
      // The bar divides: the message under it starts a fresh run, with its sender's avatar and name.
      if (row.context) row.grouped = false;
      const mark = runs?.marks[i];
      row.filing = mark?.dot ? mark : null;
      const count = runs?.closedAfter.get(i);
      if (count) withLine.push({ kind: 'filed', key: `filed-${row.key}`, count, links: this.visibleLinks(row.message) });
      i++;
    }
    if (runs?.open) withLine.push({ kind: 'filing-open', key: 'filing-open' });
    return withLine;
  }

  // ── referral cards (phase 5) ────────────────────────────────────────────────────

  /** A referral chat's cards: the summary sent and the official responses, from this practice's own records. */
  private async loadContext(): Promise<void> {
    const c = this.conversation;
    const ref = c.type === 'referral' ? c.referral : null;
    const groupId = this.chat.groupIdOfOrg(c.orgId);
    if (!ref || ref.side === 'external' || !ref.patientRef || !groupId) return;
    try {
      const data = await firstValueFrom(this.referrals.chatContext(groupId, ref.patientRef, ref.linkRef));
      if (this.conversation.id !== c.id) return;
      this.context = data.items;
    } catch {
      // The cards are an addition: a chat without them still works, and the Inbox has the same items.
      if (this.conversation.id === c.id) this.context = [];
    }
  }

  private scheduleContextRefresh(): void {
    if (this.conversation?.type !== 'referral') return;
    if (this.contextTimer) clearTimeout(this.contextTimer);
    this.contextTimer = setTimeout(() => void this.loadContext(), CONTEXT_REFRESH_MS);
  }

  asSent(item: ChatContextItem): ChatContextSent | null {
    return item.kind === 'referral_sent' ? item : null;
  }

  asReceived(item: ChatContextItem): ChatContextReceived | null {
    return item.kind === 'referral_received' ? item : null;
  }

  asResponse(item: ChatContextItem): ChatContextResponse | null {
    return item.kind === 'response' ? item : null;
  }

  asEntries(item: ChatContextItem): ChatContextEntries | null {
    return item.kind === 'entries' ? item : null;
  }

  /** "Referral", or "Earlier referral" for any but the latest this practice sent on this link. */
  sentLabel(item: ChatContextSent): string {
    const sent = this.context.filter((i) => i.kind === 'referral_sent');
    return sent.length > 1 && sent[sent.length - 1].id !== item.id ? 'Earlier referral' : 'Referral';
  }

  /** The full summary, in a small dialog (the dashboard's "Read the letter"). */
  readLetter(item: ChatContextSent | ChatContextReceived): void {
    this.letter = item;
    if (this.letterDialog) this.sheets.open(this.letterDialog);
  }

  /**
   * Review a response, as the Inbox does: the same dialog (lazy, with the patient module), so accepting it into the
   * timeline follows the same permissions and state wherever it is done. The cards are read again once it closes.
   */
  async reviewResponse(item: ChatContextResponse): Promise<void> {
    const groupId = this.chat.groupIdOfOrg(this.conversation.orgId);
    if (!groupId) return;
    try {
      const patient = await import('../../../patient/patient.module');
      const moduleRef = createNgModule(patient.PatientModule, this.injector);
      let ref: MatDialogRef<unknown, unknown>;
      if (item.responseKind === 'external_note') {
        const { ReferralNoteReviewDialogComponent } = await import('../../../patient/components/inbox/referral-note-review-dialog/referral-note-review-dialog.component');
        ref = this.dialog.open(ReferralNoteReviewDialogComponent, {
          width: '720px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false, data: { entryId: item.id, groupId }, injector: moduleRef.injector,
        });
      } else {
        const { ReferralPracticeEntryDialogComponent } = await import('../../../patient/components/inbox/referral-practice-entry-dialog/referral-practice-entry-dialog.component');
        ref = this.dialog.open(ReferralPracticeEntryDialogComponent, {
          width: '720px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false, data: { entryId: item.id, groupId, patientName: this.title() }, injector: moduleRef.injector,
        });
      }
      ref.afterClosed().subscribe(() => void this.loadContext());
    } catch {
      this.feedback.showToast('That could not be opened just now. It is also in your Inbox.');
    }
  }

  /** A referral another practice sent, still awaiting this practice's decision: the Inbox's accept / decline dialog. */
  async decideReceived(item: ChatContextReceived): Promise<void> {
    const groupId = this.chat.groupIdOfOrg(this.conversation.orgId);
    if (!groupId) return;
    try {
      const patient = await import('../../../patient/patient.module');
      const moduleRef = createNgModule(patient.PatientModule, this.injector);
      const { ReferralIncomingDialogComponent } = await import('../../../patient/components/inbox/referral-incoming-dialog/referral-incoming-dialog.component');
      this.dialog.open(ReferralIncomingDialogComponent, {
        width: '760px', maxWidth: '96vw', maxHeight: '92vh', autoFocus: false, data: { receiptId: item.id, groupId }, injector: moduleRef.injector,
      }).afterClosed().subscribe(() => void this.loadContext());
    } catch {
      this.feedback.showToast('That could not be opened just now. It is also in your Inbox.');
    }
  }

  /** The other entries a practice shared are reviewed in the Inbox, one by one. */
  openInbox(): void {
    void this.router.navigate(['/patients/inbox']);
  }

  private dayLabel(d: Date): string {
    const today = new Date();
    const yesterday = new Date(Date.now() - 86_400_000);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
  }

  trackRow(_i: number, r: Row): string {
    return r.key;
  }

  title(): string {
    return this.chat.store.titleOf(this.conversation);
  }

  replySeq(thread: Thread | null, m: ThreadMessage): number | null {
    return replyTargetSeq(thread?.messages ?? [], m);
  }

  /** Opens the conversation at the message a reply answers and highlights it, loading it if it is further back. */
  goToReply(thread: Thread | null, m: ThreadMessage): void {
    const seq = this.replySeq(thread, m);
    if (seq === null) return;
    // Clicking the same reply again scrolls to it again.
    this.scrolledToSeq = null;
    void this.chat.store.openAt(this.conversation.id, seq).catch(() => undefined);
  }

  replySnippet(thread: Thread | null, id: string | null): string {
    if (!id) return '';
    const m = thread?.messages.find((x) => x.id === id);
    if (!m) return 'an earlier message';
    if (m.deletedAt) return 'a deleted message';
    return `${m.sender?.name ?? ''}: ${(m.body ?? '').slice(0, 80)}`;
  }

  reacted(m: ThreadMessage, emoji: string, me: string | null): boolean {
    return !!me && !!m.reactions.find((r) => r.emoji === emoji)?.principalIds.includes(me);
  }

  canEdit(m: ThreadMessage, me: string | null): boolean {
    // support keeps messages as sent (M7)
    return m.kind === 'user' && !m.deletedAt && !m.pending && m.sender?.principalId === me && this.writable() && this.conversation.type !== 'support';
  }

  canDelete(m: ThreadMessage, me: string | null): boolean {
    if ((m.kind !== 'user' && m.kind !== 'forwarded') || m.deletedAt || m.pending || this.conversation.type === 'support') return false;
    return m.sender?.principalId === me || (this.conversation.type === 'channel' && this.isOrgAdmin);
  }

  /** The line under "No messages yet": what to do, or why nobody can. */
  emptyHint(): string {
    if (!this.writable()) return 'Nothing was said in this conversation.';
    switch (this.conversation.type) {
      case 'network': return 'Say hello to start the conversation. Only the two of you can see it.';
      case 'support': return 'Ask MedicAI Support anything.';
      default: return 'Say hello to start the conversation.';
    }
  }

  writable(): boolean {
    // A practice member who has not opened a patient chat before may still post: posting joins it.
    return (this.conversation.access === 'participant' || this.conversation.access === 'org_member') && !this.conversation.archivedAt
      && !this.conversation.readOnlyReason;
  }

  // ── scrolling ────────────────────────────────────────────────────────────────────

  onScroll(thread: Thread | null): void {
    const el = this.scroller?.nativeElement;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.stickToBottom = distance < 80;
    this.jumpDown = showJumpDown(distance, this.isHistoric());
    if (el.scrollTop < 120 && thread?.hasMore && !thread.loading) {
      this.restore = { height: el.scrollHeight, top: el.scrollTop };
      this.chat.store.loadOlder(this.conversation.id).catch((e: unknown) => {
        this.restore = null;
        this.fail(e);
      });
    }
  }

  // ── composing ────────────────────────────────────────────────────────────────────

  onKeydown(e: KeyboardEvent): void {
    if (this.actionPicker && this.actionPicker.results.length && !e.isComposing) {
      const p = this.actionPicker;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        p.active = (p.active + (e.key === 'ArrowDown' ? 1 : p.results.length - 1)) % p.results.length;
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const choice = p.results[p.active];
        if (choice) this.pickAction(choice);
        return;
      }
    }
    if (e.key === 'Escape' && this.actionPicker) {
      this.actionPicker = null;
      return;
    }
    if (this.personPicker && this.personPicker.results.length && !e.isComposing) {
      const p = this.personPicker;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        p.active = (p.active + (e.key === 'ArrowDown' ? 1 : p.results.length - 1)) % p.results.length;
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const choice = p.results[p.active];
        if (choice) this.pickPerson(choice);
        return;
      }
    }
    if (e.key === 'Escape' && this.personPicker) {
      this.personPicker = null;
      return;
    }
    if (this.picker && this.picker.results.length && !e.isComposing) {
      const p = this.picker;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        p.active = (p.active + (e.key === 'ArrowDown' ? 1 : p.results.length - 1)) % p.results.length;
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const choice = p.results[p.active];
        if (choice) this.pickMention(choice);
        return;
      }
    }
    if (e.key === 'Escape' && this.picker) {
      this.picker = null;
      this.pendingAction = null;
      this.pendingFrom = null;
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      this.submit();
    } else if (e.key === 'Escape') {
      this.editing = null;
      this.replyTo = null;
    }
  }

  onInput(): void {
    const now = Date.now();
    if (this.draft.trim() && now - this.lastTypingSent > TYPING_REPEAT_MS) {
      this.lastTypingSent = now;
      this.chat.store.typing(this.conversation.id, true);
    }
    this.updatePicker();
    this.scheduleSuggestions();
  }

  async submit(): Promise<void> {
    const text = this.draft.trim();
    const ready = this.files.filter((f) => f.state === 'ready' && f.view).map((f) => f.view as AttachmentView);
    if (!text && !ready.length) return;
    if (this.files.some((f) => f.state === 'uploading' || f.state === 'preparing')) return;
    const id = this.conversation.id;
    const run = this.editing || ready.length || !this.actionsOffered ? null : draftRun(text, searchActions('', 'clinical'));
    if (run) {
      this.startAction(run.action);
      return;
    }
    if (this.editing) {
      const editing = this.editing;
      this.editing = null;
      this.draft = '';
      this.chat.store.edit(editing.id, text).catch((e: unknown) => this.fail(e));
      return;
    }
    const mentioned = this.activeMentions(text);
    const people = activePeopleMentions(text, this.personMentions);
    if (mentioned.length && !(await this.confirmLinking(mentioned.map((p) => p.title)))) return;
    let patients: PatientMention[];
    try {
      patients = await this.chat.patientSubjects(mentioned);
    } catch (e) {
      this.fail(e);
      return;
    }
    // Names still unanswered when sending are asked about once more, after sending, to the sender only (§8.3).
    const unanswered = this.detect(text);
    const replyTo = this.replyTo?.id ?? null;
    this.draft = '';
    this.files = [];
    this.mentions = [];
    this.personMentions = [];
    this.personPicker = null;
    this.suggestions = [];
    this.dismissed = new Set();
    this.picker = null;
    this.replyTo = null;
    this.stickToBottom = true;
    if (this.lastTypingSent) {
      this.lastTypingSent = 0;
      this.chat.store.typing(id, false);
    }
    this.chat.store
      .send(id, text, { replyToMessageId: replyTo, patients, attachments: ready, ...(people.length ? { mentions: people } : {}) })
      .then((saved) => {
        if (saved && unanswered.length) this.askAfterSend.set(saved.id, unanswered);
        // Network chat (2026-09-22): the patients just linked, checked against the person on the other end.
        if (saved && patients.length) this.checkNetworkReferrals(saved.id, patients);
        if (saved && patients.length) this.checkConnectPatients(saved.id, patients);
      })
      .catch((e: unknown) => this.fail(e));
  }

  canSend(): boolean {
    return (!!this.draft.trim() || this.files.some((f) => f.state === 'ready'))
      && !this.files.some((f) => f.state === 'uploading' || f.state === 'preparing');
  }

  // ── files (M5) ───────────────────────────────────────────────────────────────────

  /**
   * Files may be attached here. In a referral chat with a clinician outside MedicAI, each message with files is one of the
   * referral's documents: the chat store gets leave for it from this platform (document allowance, 2026-09-29). A
   * network chat with them stays text only. Files already sent still show and open.
   */
  get filesAllowed(): boolean {
    return conversationAcceptsFiles(this.conversation);
  }

  pickFiles(): void {
    this.filePicker?.nativeElement.click();
  }

  onFilesPicked(input: HTMLInputElement): void {
    const picked = Array.from(input.files ?? []);
    input.value = '';
    this.addFiles(picked);
  }

  /** Pasted screenshots and files go with the next message; pasted text is left to the textarea. */
  onPaste(e: ClipboardEvent): void {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (!files.length || !this.writable() || this.editing || !this.filesAllowed) return;
    e.preventDefault();
    const now = new Date();
    this.addFiles(files.map((f, i) => new File([f], pastedFileName(f, i, now), { type: f.type })));
  }

  /**
   * A paste anywhere else in the conversation (the messages were clicked last): files go with the next message, as in
   * the box; text goes into the box, at its end, and the box takes the focus.
   */
  @HostListener('paste', ['$event'])
  onThreadPaste(e: ClipboardEvent): void {
    const target = e.target as HTMLElement | null;
    if (!target || target.closest('textarea, input, [contenteditable="true"]') || !this.writable()) return;
    if (e.clipboardData?.files.length) {
      this.onPaste(e);
      return;
    }
    const text = e.clipboardData?.getData('text/plain') ?? '';
    if (!text) return;
    e.preventDefault();
    this.draft = this.draft ? `${this.draft}${/\s$/.test(this.draft) ? '' : ' '}${text}` : text;
    setTimeout(() => {
      const el = this.composer?.nativeElement;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  }

  /** Files dragged over ANY part of the conversation, not only the box: the whole pane takes them. */
  @HostListener('dragover', ['$event'])
  onDragOver(e: DragEvent): void {
    if (!this.writable() || this.editing || !this.filesAllowed || !Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    this.dragging = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(e: DragEvent): void {
    // Leaving for a child of the conversation is not leaving it.
    if (!this.host.nativeElement.contains(e.relatedTarget as Node | null)) this.dragging = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(e: DragEvent): void {
    this.dragging = false;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (!files.length || !this.writable() || this.editing || !this.filesAllowed) return;
    e.preventDefault();
    this.addFiles(files);
  }

  // ── copy ─────────────────────────────────────────────────────────────────────────

  canCopy(m: ThreadMessage): boolean {
    return !m.pending && !!copyContent(m);
  }

  /** "Copy": the message's text, or for a message that is only a picture, the picture itself. */
  async copy(m: ThreadMessage): Promise<void> {
    const content = copyContent(m);
    if (!content) return;
    if (content.kind === 'text') {
      const copied = await navigator.clipboard?.writeText(content.text).then(() => true, () => false) || copyTextTheOldWay(content.text);
      if (copied) this.snack.open('Copied', undefined, { duration: 1500 });
      else this.snack.open('That could not be copied here. Select the text and copy it instead.', 'OK', { duration: 4000 });
      return;
    }
    try {
      const url = this.imageUrl(content.attachment) ?? (await this.chat.store.fileUrl(content.attachment.id));
      const blob = await (await fetch(url)).blob();
      // Browsers put only PNG on the clipboard as a picture.
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': await toPng(blob) })]);
      this.snack.open('Picture copied', undefined, { duration: 1500 });
    } catch {
      this.snack.open('That could not be copied here. Open it and copy it from there.', 'OK', { duration: 4000 });
    }
  }

  /** Checks, prepares (a large photo is shrunk; HEIC becomes JPEG where the browser can) and uploads each file. */
  addFiles(picked: File[]): void {
    if (!this.filesAllowed) {
      this.snack.open(fileErrorText('EXTERNAL_REFERRAL_NO_FILES'), 'OK', { duration: 6000 });
      return;
    }
    const room = MAX_FILES - this.files.length;
    if (picked.length > room) this.snack.open(fileErrorText('TOO_MANY_FILES'), 'OK', { duration: 4000 });
    for (const file of picked.slice(0, Math.max(0, room))) {
      const refused = checkFile(file, file.name);
      if (refused) {
        this.snack.open(fileErrorText(refused, file.name), 'OK', { duration: 6000 });
        continue;
      }
      const entry: DraftFile = { key: `${Date.now()}-${Math.random()}`, name: file.name, state: 'preparing', progress: 0 };
      this.files = [...this.files, entry];
      void prepareImage(file, file.name).then((prepared) => {
        if (!this.files.some((f) => f.key === entry.key)) return; // removed meanwhile
        this.setFile(entry.key, { name: prepared.fileName, blob: prepared.file });
        this.upload(entry.key);
      });
    }
  }

  private upload(key: string): void {
    const f = this.files.find((x) => x.key === key);
    if (!f?.blob) return;
    const cancel = new AbortController();
    const conversationId = this.conversation.id;
    this.setFile(key, { state: 'uploading', progress: 0, error: undefined, cancel });
    this.chat.store.uploadFile(conversationId, f.blob, f.name, {
      signal: cancel.signal,
      onProgress: (p) => this.setFile(key, { progress: Math.floor((p.sentBytes / p.totalBytes) * 100) }),
    }).then(
      (view) => this.setFile(key, { state: 'ready', view, progress: 100, cancel: undefined }),
      (e: unknown) => {
        const code = e instanceof HubRequestError ? e.code : 'UPLOAD_FAILED';
        if (code === 'CANCELLED') return;
        this.setFile(key, { state: 'failed', error: fileErrorText(code, f.name), cancel: undefined });
      },
    );
  }

  /** A failed file again: it continues from the chunks the hub already has. */
  retryFile(f: DraftFile): void {
    this.upload(f.key);
  }

  private setFile(key: string, change: Partial<DraftFile>): void {
    this.files = this.files.map((f) => (f.key === key ? { ...f, ...change } : f));
  }

  removeFile(f: DraftFile): void {
    f.cancel?.abort();
    this.files = this.files.filter((x) => x.key !== f.key);
  }

  // ── delivered and read ticks ──────────────────────────────────────────────────────

  /** The tick for one of my own messages; null for everyone else's (they never show ticks). */
  tick(m: ThreadMessage): MessageTick | null {
    return this.chat.store.tickOf(this.conversation.id, m);
  }

  /**
   * The photo beside the other side's first bubble of a run (2026-09-22, as the referrals dashboard): in every kind of
   * conversation, a direct message too, since the avatar is now where the eye finds who spoke.
   */
  lineAvatar(m: ThreadMessage): string | null {
    return m.sender?.avatar ? this.chat.store.avatarUrl(m.sender.principalId, m.sender.avatar) : null;
  }

  /** Their initials, for the same spot when they have no photo. */
  senderInitials(m: ThreadMessage): string {
    return initialsOf(m.sender?.name);
  }

  senderHue(m: ThreadMessage): number {
    const id = m.sender?.principalId ?? '';
    return this.hues.get(id) ?? avatarHue(id);
  }

  /** In a room (a channel, a group, a patient chat) a tick says nothing useful: how many have read it does. */
  readCount(m: ThreadMessage): { read: number; of: number } | null {
    return this.chat.store.readCountOf(this.conversation.id, m);
  }

  /** The thread is showing an older page, opened from a search hit. */
  /** The loader to show: the first page in the middle, older ones above the messages. */
  loader(thread: Thread | null): 'first' | 'older' | null {
    return threadLoader(thread);
  }

  isHistoric(): boolean {
    return !!this.chat.store.snapshot.threads[this.conversation.id]?.historic;
  }

  /** The message a search hit asked for, marked while it is on screen. */
  pinnedSeq(): number | null {
    return this.chat.store.snapshot.threads[this.conversation.id]?.pinnedSeq ?? null;
  }

  /** New messages from others below where the reader is, on the down button. */
  newMessagesBelow(messages: ThreadMessage[], me: string | null): number {
    return newBelow(messages, this.seenSeq, me);
  }

  /** The down button: to the latest message, loading it first when an older part is showing. */
  jumpToLatest(): void {
    this.jumpDown = false;
    if (this.isHistoric()) {
      this.backToLatest();
      return;
    }
    const el = this.scroller?.nativeElement;
    if (!el) return;
    this.stickToBottom = true;
    el.scrollTo({ top: el.scrollHeight, behavior: document.visibilityState === 'visible' ? 'smooth' : 'auto' });
  }

  backToLatest(): void {
    void this.chat.store.backToLatest(this.conversation.id).catch((e: unknown) => this.fail(e));
  }

  tickIcon(t: MessageTick): string {
    return t === 'sending' ? 'schedule' : t === 'sent' ? 'done' : 'done_all';
  }

  tickLabel(t: MessageTick): string {
    const others = this.conversation.receipts?.others ?? 0;
    const everyone = others > 1 ? 'everyone' : 'them';
    if (t === 'sending') return 'Sending…';
    if (t === 'sent') return 'Sent';
    if (t === 'delivered') return `Delivered to ${everyone}`;
    return `Read by ${everyone}`;
  }

  /** Worth opening the "who has read it" list: more than one other person can be in different places. */
  canListReceipts(): boolean {
    // Not on the asker's side of support: the list would name the agents.
    return (this.conversation.receipts?.others ?? 0) > 1 && !this.supportAsker();
  }

  /** The practice user's side of their support chat, where support's answers carry no agent's name. */
  supportAsker(): boolean {
    return this.conversation.type === 'support' && this.conversation.support?.side === 'requester';
  }

  /** A pinned message's sender as the asker sees it: theirs by name, the rest MedicAI Support. */
  private supportPins(pins: PinView[]): PinView[] {
    if (!this.supportAsker()) return pins;
    const myName = this.chat.store.snapshot.me?.displayName;
    return pins.map((p) => (p.senderName && p.senderName !== myName ? { ...p, senderName: SUPPORT_NAME } : p));
  }

  /** Who has read this message, and who has only received it. Opened from the tick in a group. */
  async showReceipts(m: ThreadMessage): Promise<void> {
    if (!this.canListReceipts()) return;
    try {
      const people = await this.chat.store.receiptsFor(this.conversation.id);
      this.receipts = {
        read: people.filter((p) => p.readSeq >= m.seq),
        delivered: people.filter((p) => p.readSeq < m.seq && p.deliveredSeq >= m.seq),
        waiting: people.filter((p) => p.deliveredSeq < m.seq),
      };
      if (this.receiptsDialog) this.sheets.open(this.receiptsDialog);
    } catch (e) {
      this.fail(e);
    }
  }

  isMedia(a: AttachmentView): 'audio' | 'video' | null {
    return a.contentType.startsWith('audio/') ? 'audio' : a.contentType.startsWith('video/') ? 'video' : null;
  }

  isImage(a: AttachmentView): boolean {
    return a.contentType.startsWith('image/');
  }

  fileIcon(a: AttachmentView): string {
    if (a.contentType === 'application/pdf') return 'picture_as_pdf';
    if (a.contentType.startsWith('audio/')) return 'audiotrack';
    if (a.contentType.startsWith('video/')) return 'movie';
    if (a.contentType.startsWith('image/')) return 'image';
    return 'description';
  }

  fileSize(bytes: number): string {
    return bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  imageUrl(a: AttachmentView): string | null {
    const hit = this.fileUrls.get(a.id);
    return hit && Date.now() - hit.at < FILE_URL_TTL_MS ? hit.url : null;
  }

  /** Image previews need a (short-lived) URL each; fetched once per image while shown. */
  private loadImageUrls(messages: ThreadMessage[]): void {
    for (const m of messages) {
      for (const a of m.attachments ?? []) {
        if (!this.isImage(a) || this.imageUrl(a) || this.fileUrlLoading.has(a.id) || m.deletedAt) continue;
        if (a.contentType === 'image/heic') continue; // most browsers cannot show HEIC: a chip, opened as a download
        this.fileUrlLoading.add(a.id);
        this.chat.store.fileUrl(a.id).then(
          (url) => this.fileUrls.set(a.id, { url, at: Date.now() }),
          () => undefined,
        ).finally(() => this.fileUrlLoading.delete(a.id));
      }
    }
  }

  /** Audio and video play in the thread: the URL is asked for on the first click (it lives an hour for media). */
  playing = new Set<string>();
  async play(a: AttachmentView): Promise<void> {
    try {
      const url = await this.chat.store.fileUrl(a.id);
      this.fileUrls.set(a.id, { url, at: Date.now() });
      this.playing = new Set(this.playing).add(a.id);
    } catch (e) {
      this.fail(e);
    }
  }

  mediaUrl(a: AttachmentView): string | null {
    return this.playing.has(a.id) ? this.fileUrls.get(a.id)?.url ?? null : null;
  }

  /**
   * Opens a file. A photo opens in the app's own viewer (the one the patient file uses: zoom, rotate, page through), so
   * nobody leaves chat to look at it. Anything else opens in a new tab, where the browser shows a PDF or downloads the rest.
   */
  async openFile(a: AttachmentView): Promise<void> {
    if (isInlinePhoto(a)) {
      await this.openPhotos([a], 0);
      return;
    }
    // Open the tab now (still inside the click), then point it at the URL: a popup opened after an await is blocked.
    const tab = window.open('', '_blank');
    try {
      const url = await this.chat.store.fileUrl(a.id);
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch (e) {
      tab?.close();
      this.fail(e);
    }
  }

  private readonly photoGrids = new WeakMap<readonly AttachmentView[], MessagePhotos>();
  /** A message's photos as one grid, worked out once per attachment list rather than on every check. */
  photos(m: ThreadMessage): MessagePhotos {
    const list = m.attachments ?? [];
    let hit = this.photoGrids.get(list);
    if (!hit) {
      hit = messagePhotos(list);
      this.photoGrids.set(list, hit);
    }
    return hit;
  }

  private readonly otherFileLists = new WeakMap<readonly AttachmentView[], AttachmentView[]>();
  /** The attachments that are not in the photo grid (videos, audio, documents), drawn below it; null when there are none. */
  otherFiles(m: ThreadMessage): AttachmentView[] | null {
    const list = m.attachments ?? [];
    let hit = this.otherFileLists.get(list);
    if (!hit) {
      hit = list.filter((a) => !isInlinePhoto(a));
      this.otherFileLists.set(list, hit);
    }
    return hit.length ? hit : null;
  }

  /** Opens the viewer over every photo of the message, at the tapped one. */
  async openPhotos(photos: AttachmentView[], index: number): Promise<void> {
    try {
      const fileObjects = await Promise.all(photos.map(async (a) => ({
        id: a.id,
        name: a.fileName,
        type: a.contentType,
        // The hub's links are short-lived: asked for as the viewer opens when the thumbnail's has gone stale.
        url: this.imageUrl(a) ?? (await this.chat.store.fileUrl(a.id)),
      })));
      this.dialog.open(ImageDialogComponent, { data: photoViewerData(fileObjects, index), panelClass: 'image-modal-panel' });
    } catch (e) {
      this.fail(e);
    }
  }

  // ── smart patient mentions ───────────────────────────────────────────────────────

  /** The caret moved without typing (a click): the picker follows it. */
  onCaret(): void {
    this.updatePicker();
  }

  /** What this conversation's composer offers (owner, 2026-09-21): no @ or # in support, no # in a referral chat. */
  get features(): ComposeFeatures {
    return composeFeatures(this.conversation.type);
  }

  /** Just "Message": the @ and # chips over the field say what they do. */
  placeholder(): string {
    return 'Message';
  }

  triggerChips(): { trigger: Trigger; label: string }[] {
    return triggerChips(this.features, this.actionsOffered);
  }

  /** Actions need a patient, so they are offered where a patient can be named. */
  get actionsOffered(): boolean {
    return this.features.patients && searchActions('', 'clinical').length > 0;
  }

  trackTrigger(_i: number, c: { trigger: Trigger }): string {
    return c.trigger;
  }

  emptyPickerHint(trigger: Trigger, query: string): string {
    return emptyPickerHint(trigger, query);
  }

  /** A chip typed its trigger: the caret goes to the end and the picker opens as if it had been typed. */
  addTrigger(trigger: Trigger): void {
    if (trigger === '/') {
      this.openActionSheet();
      return;
    }
    this.draft = withTrigger(this.draft, trigger);
    setTimeout(() => {
      const el = this.composer?.nativeElement;
      if (!el) return;
      el.focus();
      el.setSelectionRange(this.draft.length, this.draft.length);
      this.updatePicker();
    });
  }

  /** Opens, narrows or closes the `#` picker for the `#query` the caret is in (or the `@` one for an `@query`). */
  private updatePicker(): void {
    const el = this.composer?.nativeElement;
    const caret = el?.selectionStart ?? this.draft.length;
    const slash = this.editing || !this.actionsOffered ? null : slashQueryBefore(this.draft.slice(0, caret));
    if (slash) {
      this.picker = null;
      this.personPicker = null;
      this.actionPicker = { start: slash.start, end: caret, query: slash.query, results: searchActions(slash.query, 'clinical'), active: 0 };
      return;
    }
    this.actionPicker = null;
    const at = this.editing || !this.features.people ? null : atQueryBefore(this.draft.slice(0, caret));
    if (at) {
      this.picker = null;
      const people = this.chat.store.mentionableNow(this.conversation.id);
      const taken = new Set(activePeopleMentions(this.draft, this.personMentions));
      this.personPicker = { start: at.start, end: caret, query: at.query, results: searchPeople(people, at.query, { limit: 6, exclude: taken }), active: 0 };
      if (!people.length) {
        // First use in this conversation: ask, then narrow again once the names are here.
        void this.chat.store.mentionable(this.conversation.id).then(() => this.personPicker && this.updatePicker(), () => undefined);
      }
      return;
    }
    this.personPicker = null;
    const m = this.editing || !this.features.patients ? null : /(?:^|\s)#([^#\n]{0,40})$/.exec(this.draft.slice(0, caret));
    const query = m?.[1] ?? null;
    if (query === null || query.startsWith(' ') || query.split(/\s+/).length > 4) {
      this.picker = null;
      return;
    }
    const ctx = this.chat.mentionContextNow(this.conversation.orgId);
    const results = query.trim()
      ? searchPatients(ctx.index, query, { limit: 6, recent: ctx.recent }).filter((c) => c.id !== this.conversation.patientRef)
      : [];
    this.picker = { start: caret - query.length - 1, end: caret, results, active: 0 };
  }

  /** The patient a money card's Chat button asked for: tagged at the start of the box, unless already tagged. */
  private applyPrefill(): void {
    const patient = this.chat.store.takePrefill(this.conversation.id);
    const next = patient ? prefillDraft(this.draft, this.mentions, patient) : null;
    if (!next) return;
    this.draft = next.draft;
    this.mentions = next.mentions;
    setTimeout(() => {
      const el = this.composer?.nativeElement;
      if (!el) return;
      el.focus();
      el.setSelectionRange(this.draft.length, this.draft.length);
    });
  }

  pickMention(c: PatientCandidate): void {
    const p = this.picker;
    if (!p) return;
    const text = `#${c.title}`;
    this.draft = `${this.draft.slice(0, p.start)}${text} ${this.draft.slice(p.end)}`;
    this.mentions = [...this.mentions.filter((m) => m.patientRef !== c.id), { patientRef: c.id, title: c.title, text }];
    this.picker = null;
    if (this.pendingAction) {
      const action = this.pendingAction;
      this.pendingAction = null;
      this.runAction(action, { patientRef: c.id, title: c.title });
      return;
    }
    const caret = p.start + text.length + 1;
    setTimeout(() => {
      const el = this.composer?.nativeElement;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
    this.scheduleSuggestions();
  }

  /**
   * A `/` pick: the action's word goes into the draft. Picked after the text was typed, it runs at once; picked first,
   * it runs when the draft is sent, so the text can follow it.
   */
  pickAction(action: ChatAction): void {
    const p = this.actionPicker;
    if (!p) return;
    this.draft = `${this.draft.slice(0, p.start)}${action.label} ${this.draft.slice(p.end)}`;
    this.actionPicker = null;
    this.placeAction(action, p.start + action.label.length + 1);
  }

  /** The `/ Action` chip: the action sheet in the chat window, recently used first, greyed where it cannot run. */
  openActionSheet(): void {
    this.actionTiles = actionSheetTiles(searchActions('', 'clinical'), this.conversation, loadRecentActions(this.storage(), this.meId()));
    if (this.actionSheet) this.sheets.open(this.actionSheet);
  }

  /** A pick on the action sheet: its word goes after the draft, then as a `/` pick. */
  pickFromSheet(tile: ActionTile): void {
    if (!tile.available) return;
    this.sheets.closeAll();
    this.draft = withAction(this.draft, tile.action.label);
    this.placeAction(tile.action, this.draft.length);
  }

  /** The action word is in the draft: with text typed it runs at once; alone, it waits for Send so the text can follow. */
  private placeAction(action: ChatAction, caret: number): void {
    if (actionDraftText(this.draft, this.draftActionLabels(), []).length) {
      // the first action typed is the one that runs
      this.startAction(draftRun(this.draft, searchActions('', 'clinical'))?.action ?? action);
      return;
    }
    setTimeout(() => {
      const el = this.composer?.nativeElement;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }

  /** Every action word in the draft: none of them goes to the action's page. */
  private draftActionLabels(): string[] {
    return actionsInDraft(this.draft, searchActions('', 'clinical')).map((a) => a.label);
  }

  /** The action runs for the one patient in play; with none or several, the `#` picker asks first. */
  private startAction(action: ChatAction): void {
    this.pendingFrom = null;
    const patients = actionPatients(this.conversation, this.activeMentions(this.draft), this.filing);
    if (patients.length === 1) {
      this.runAction(action, patients[0]!);
      return;
    }
    this.pendingAction = action;
    this.addTrigger('#');
  }

  /**
   * Opens the action's page by way of the patient's file, filled in from the draft (or from the message "Make a task"
   * started from), and clears the draft. The due day comes from the text, the assignees from the @people.
   */
  private runAction(action: ChatAction, patient: PatientMention): void {
    const from = this.pendingFrom;
    const drop = this.mentions.filter((m) => m.patientRef === patient.patientRef).map((m) => m.text);
    const written = from ? from.text : actionDraftText(this.draft, this.draftActionLabels(), drop);
    // one action runs per message: say so when the draft named others
    const notice = from ? null : draftRun(this.draft, searchActions('', 'clinical'))?.notice;
    if (notice) this.snack.open(notice, 'OK', { duration: 6000 });
    const principals = from ? from.principals : activePeopleMentions(this.draft, this.personMentions);
    const people = this.chat.store.mentionableNow(this.conversation.id) as (MentionablePerson & { clinicalUserId?: string })[];
    const id = this.conversation.id;
    // a booking reads its day and time from the text; a task its due day, and the @people become its assignees
    // a note takes the text as written, lines and all
    const prefill = action.id === 'appointment'
      ? bookingPrefill(written, localDay())
      : action.id === 'note'
        ? { text: written }
        : { ...actionPrefillExtras(written, localDay(), principals, people), text: withoutAssignees(written, principals, people) };
    this.pendingFrom = null;
    saveRecentActions(this.storage(), this.meId(), rememberAction(loadRecentActions(this.storage(), this.meId()), action.id));
    this.draft = '';
    this.mentions = [];
    this.personMentions = [];
    this.picker = null;
    this.personPicker = null;
    this.actionPicker = null;
    this.pendingAction = null;
    this.suggestions = [];
    this.chat.store.saveDraft(id, '');
    if (this.lastTypingSent) {
      this.lastTypingSent = 0;
      this.chat.store.typing(id, false);
    }
    // in the conversation's practice, whichever one this app has selected
    const practice = this.chat.groupIdOfOrg(this.conversation.orgId) ?? undefined;
    void this.router.navigateByUrl(actionPath(action.id, patient.patientRef, { ...prefill, chat: id, practice }));
  }

  actionCardText(card: ActionCard): string {
    return actionCardText(card);
  }

  /** A task, booking or note still standing: drawn as a card across the thread, not as a bubble. */
  isCard(m: ThreadMessage): boolean {
    return !!m.card && !m.deletedAt;
  }

  /** The card's line under its title: due and who has it while open, who did it once done. */
  actionCardStatus(card: ActionCard): string | null {
    return actionCardStatus(card, localDay());
  }

  /** A card's Done: the task is completed in clinical, and the card follows when the hub sends it changed. */
  async completeActionCard(card: ActionCard): Promise<void> {
    if (this.completing.has(card.recordId)) return;
    this.completing.add(card.recordId);
    try {
      await this.chat.markTaskDone(card.recordId);
    } catch {
      this.snack.open('Could not mark the task done.', 'OK', { duration: 4000 });
    } finally {
      this.completing.delete(card.recordId);
    }
  }

  /** A card's icon: a booking's calendar, a note's page, a task's tick. */
  actionCardIcon(card: ActionCard): string {
    return card.type === 'appointment' ? 'event' : card.type === 'note' ? 'description' : 'task_alt';
  }

  /** The card's kind as its header reads it. */
  actionCardKind(card: ActionCard): string {
    return card.type === 'appointment' ? 'APPOINTMENT' : card.type === 'note' ? 'NOTE' : 'TASK';
  }

  /** The card's outline colour, from where it stands. */
  actionCardOutline(card: ActionCard): string {
    return cardOutline(cardState(card, localDay()));
  }

  /** Done, completed, cancelled or not attended: the title is struck through and muted. */
  actionCardClosed(card: ActionCard): boolean {
    return ['done', 'completed', 'cancelled', 'did_not_attend'].includes(cardState(card, localDay()));
  }

  /** The status line takes the outline colour unless the card is plainly open, booked or a note. */
  actionCardStatusTinted(card: ActionCard): boolean {
    return !['open', 'booked', 'note'].includes(cardState(card, localDay()));
  }

  /** What the card's own menu offers (right click or long press on a card). */
  cardMenu(m: ThreadMessage): CardActions {
    return cardActions(m, { me: this.meId(), writable: this.writable(), receipts: this.canListReceipts(), support: this.conversation.type === 'support' });
  }

  bookingButtons(card: ActionCard): { confirm: boolean; cancel: boolean } | null {
    const b = bookingButtons(card);
    return b.confirm || b.cancel ? b : null;
  }

  /**
   * A booking card's Confirm or Cancel, in clinical as the calendar's status menu does it (a cancellation emails the
   * patient as it would there, so it asks first). The card follows when the hub sends it changed.
   */
  async setBookingStatus(card: ActionCard, status: 'confirmed' | 'cancelled'): Promise<void> {
    if (this.completing.has(card.recordId)) return;
    if (status === 'cancelled') {
      const yes = await this.sheets.confirm({
        title: 'Cancel this appointment?',
        message: `${actionCardText(card)}. The patient is told it is cancelled, as when it is cancelled on the calendar.`,
        confirmText: 'Cancel appointment',
        cancelText: 'Keep it',
        danger: true,
      });
      if (!yes) return;
    }
    this.completing.add(card.recordId);
    try {
      await this.chat.setAppointmentStatus(card.recordId, status);
    } catch {
      this.snack.open(status === 'confirmed' ? 'Could not confirm the appointment.' : 'Could not cancel the appointment.', 'OK', { duration: 4000 });
    } finally {
      this.completing.delete(card.recordId);
    }
  }

  /** "Make a task" (and "Book for this patient") is offered on what a person wrote, where actions can run. */
  canMakeTask(m: ThreadMessage): boolean {
    return this.actionsOffered && !!m.sender && !!m.body?.trim() && !m.card && !m.deletedAt && !m.pending
      && (m.kind === 'user' || m.kind === 'linked_copy' || m.kind === 'forwarded');
  }

  /**
   * "Make a task" from a message: its text (without the patients it names) and its @people go to the add-task page
   * for the patient it is about; with none or several, the `#` picker asks first.
   */
  makeTask(m: ThreadMessage, actionId: 'task' | 'appointment' | 'note' = 'task'): void {
    const action = searchActions('', 'clinical').find((a) => a.id === actionId);
    if (!action || !m.body) return;
    const index = this.chat.mentionContextNow(this.conversation.orgId).index;
    const about = messageActionPatients(m, (ref) => index.patients.get(ref)?.title ?? null);
    // "Add to notes" keeps who said it and when
    const text = actionId === 'note' ? noteDraftFrom([m], localClock) : messageTaskText(m.body, about.map((p) => p.title));
    this.pendingFrom = { text, principals: m.mentions?.principals ?? [] };
    this.runForPatients(action, about);
  }

  /**
   * "Add to notes" on a patient heading: the messages under it, one line each, to a note for the patient it names
   * (the `#` picker asks which when it names several).
   */
  addRunToNotes(m: ThreadMessage): void {
    const action = searchActions('', 'clinical').find((a) => a.id === 'note');
    const messages = this.chat.store.snapshot.threads[this.conversation.id]?.messages ?? [];
    const run = runFrom(messages, m.id, (x) => (x.kind === 'system' ? '' : patientContextKey(this.visibleLinks(x))));
    const text = noteDraftFrom(run, localClock);
    if (!action || !text) return;
    const about = this.visibleLinks(m).filter((l) => l.patientRef).map((l) => ({ patientRef: l.patientRef!, title: l.name || 'this patient' }));
    this.pendingFrom = { text, principals: [] };
    this.runForPatients(action, about);
  }

  /** The action runs for the one patient in play; with none or several, the `#` picker asks first. */
  private runForPatients(action: ChatAction, about: PatientMention[]): void {
    const patients = actionPatients(this.conversation, about, this.filing);
    if (patients.length === 1) {
      this.runAction(action, patients[0]!);
      return;
    }
    this.pendingAction = action;
    this.addTrigger('#');
  }

  private meId(): string {
    return this.chat.store.snapshot.me?.principalId ?? 'anyone';
  }

  /** This browser's storage, or null where it cannot be reached (a private window, blocked site data). */
  private storage(): Storage | null {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
      return null;
    }
  }

  /** A card's Open: the record's page, after its patient's file has loaded (as the action that made it). */
  openActionCard(card: ActionCard): void {
    void this.router.navigateByUrl(actionCardPath(card, this.chat.groupIdOfOrg(this.conversation.orgId)));
  }

  /** The icon for an action in the picker. */
  actionIcon(action: ChatAction): string {
    return action.icon === 'task' ? 'task_alt' : action.icon === 'event' ? 'event' : action.icon === 'note' ? 'description' : 'bolt';
  }

  /** An `@` pick: "@Their Name " goes into the sentence, and they are mentioned when it is sent. */
  pickPerson(person: MentionablePerson): void {
    const p = this.personPicker;
    if (!p) return;
    const text = mentionText(person.name);
    this.draft = `${this.draft.slice(0, p.start)}${text} ${this.draft.slice(p.end)}`;
    this.personMentions = [...this.personMentions.filter((m) => m.principalId !== person.principalId), { principalId: person.principalId, name: person.name, text }];
    this.personPicker = null;
    const caret = p.start + text.length + 1;
    setTimeout(() => {
      const el = this.composer?.nativeElement;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }

  /** A message body in pieces, with each "@Name" of someone it mentions marked. */
  segments(m: ThreadMessage): BodySegment[] {
    const body = m.body ?? '';
    if (!m.mentions?.principals?.length) return [{ text: body }];
    const people = this.chat.store.mentionedPeople(this.conversation.id, m);
    const key = `${body}|${people.map((p) => p.principalId + p.name).join(',')}`;
    const hit = this.segmentCache.get(m.id);
    if (hit?.key === key) return hit.segments;
    const segments = mentionSegments(body, people);
    this.segmentCache.set(m.id, { key, segments });
    return segments;
  }

  personPhoto(p: MentionablePerson): string | null {
    return p.avatar ? this.chat.store.avatarUrl(p.principalId, p.avatar) : null;
  }

  personInitials(p: MentionablePerson): string {
    return initialsOf(p.name);
  }

  personHue(p: MentionablePerson): number {
    return this.hues.get(p.principalId) ?? avatarHue(p.principalId);
  }

  trackSegment(i: number): number {
    return i;
  }

  /** The draft's `#` mentions whose text is still in it, one per patient. */
  private activeMentions(text: string): PatientMention[] {
    const seen = new Set<string>();
    return this.mentions
      .filter((m) => text.includes(m.text) && !seen.has(m.patientRef) && seen.add(m.patientRef))
      .map((m) => ({ patientRef: m.patientRef, title: m.title }));
  }

  /**
   * Patient names in `text`, leaving out `#` mentions, @-mentioned colleagues (a colleague named like a patient is not
   * one), the patient whose chat this is, and names already declined.
   */
  private detect(text: string): Detection[] {
    // No "did you mean this patient?" where patients are not linked (support, referral chats).
    if (!this.features.patients) return [];
    const ctx = this.chat.mentionContextNow(this.conversation.orgId);
    if (!ctx.index.size) return [];
    const spans: { start: number; end: number }[] = [];
    for (const m of this.mentions) {
      for (let at = text.indexOf(m.text); at >= 0; at = text.indexOf(m.text, at + 1)) spans.push({ start: at, end: at + m.text.length });
    }
    spans.push(...peopleMentionSpans(text, this.personMentions));
    const excludeIds = new Set([...this.mentions.map((m) => m.patientRef), ...(this.conversation.patientRef ? [this.conversation.patientRef] : [])]);
    return detectPatients(ctx.index, text, { skipSpans: spans, excludeIds, recent: ctx.recent, colleagueSurnames: ctx.colleagueSurnames })
      .filter((d) => !this.dismissed.has(normalise(d.text)));
  }

  private scheduleSuggestions(): void {
    if (this.suggestTimer) clearTimeout(this.suggestTimer);
    if (this.editing || !this.draft.trim()) {
      this.suggestions = [];
      return;
    }
    this.suggestTimer = setTimeout(() => (this.suggestions = this.detect(this.draft)), SUGGEST_AFTER_MS);
  }

  /** "Link to patient?" accepted before sending: the name becomes a `#` mention, linked when the message is sent. */
  acceptSuggestion(d: Detection, c: PatientCandidate): void {
    if (this.draft.slice(d.start, d.end) !== d.text) {
      this.suggestions = this.detect(this.draft);
      return;
    }
    const text = `#${c.title}`;
    this.draft = `${this.draft.slice(0, d.start)}${text}${this.draft.slice(d.end)}`;
    this.mentions = [...this.mentions.filter((m) => m.patientRef !== c.id), { patientRef: c.id, title: c.title, text }];
    this.suggestions = this.detect(this.draft);
  }

  dismissSuggestion(d: Detection): void {
    this.dismissed.add(normalise(d.text));
    this.suggestions = this.suggestions.filter((x) => x !== d);
  }

  askFor(messageId: string): Detection[] {
    return this.askAfterSend.get(messageId) ?? [];
  }

  /** "Did you mean …?" answered with a patient: the sent message is linked into that patient's chat. */
  async linkFromAsk(m: ThreadMessage, d: Detection, c: PatientCandidate): Promise<void> {
    if (!(await this.confirmLinking([c.title]))) return;
    try {
      await this.chat.linkMessage(m.id, c);
      this.checkNetworkReferrals(m.id, [{ patientRef: c.id, title: c.title }]);
      this.checkConnectPatients(m.id, [{ patientRef: c.id, title: c.title }]);
      this.answerAsk(m.id, d);
      this.snack.open(`Filing this conversation to ${c.title}'s chat.`, 'OK', { duration: 3000 });
    } catch (e) {
      this.fail(e);
    }
  }

  /** "Not a patient": not asked again for this message. */
  answerAsk(messageId: string, d: Detection): void {
    const left = this.askFor(messageId).filter((x) => x !== d);
    if (left.length) this.askAfterSend.set(messageId, left);
    else this.askAfterSend.delete(messageId);
  }

  canLink(m: ThreadMessage): boolean {
    return this.features.patients && (m.kind === 'user' || m.kind === 'forwarded') && !m.deletedAt && !m.pending && this.chat.mentionContextNow(this.conversation.orgId).index.size > 0;
  }

  startLink(m: ThreadMessage): void {
    this.linking = m;
    this.linkQuery = '';
    this.linkResults = [];
    setTimeout(() => this.linkInput?.nativeElement.focus());
  }

  onLinkQuery(): void {
    const ctx = this.chat.mentionContextNow(this.conversation.orgId);
    this.linkResults = searchPatients(ctx.index, this.linkQuery, { limit: 6, recent: ctx.recent }).filter((c) => c.id !== this.conversation.patientRef);
  }

  async pickLink(c: PatientCandidate): Promise<void> {
    const m = this.linking;
    if (!m || !(await this.confirmLinking([c.title]))) return;
    this.linking = null;
    try {
      await this.chat.linkMessage(m.id, c);
      this.checkNetworkReferrals(m.id, [{ patientRef: c.id, title: c.title }]);
      this.checkConnectPatients(m.id, [{ patientRef: c.id, title: c.title }]);
      this.snack.open(`Filing this conversation to ${c.title}'s chat.`, 'OK', { duration: 3000 });
    } catch (e) {
      this.fail(e);
    }
  }

  openLinked(conversationId: string): void {
    this.chat.openConversation(conversationId);
  }

  /**
   * The patient's FILE, from a link chip (2026-09-21). Following a link used to offer only the patient's chat, which
   * is the right place to keep reading and the wrong one when what you actually want is the record.
   */
  openPatientFile(patientRef: string | null): void {
    if (!patientRef) return;
    void this.router.navigate(['/patients', { outlets: { patient: ['file', patientRef] } }]);
  }

  // ── forwarding ───────────────────────────────────────────────────────────────────

  /** Anything someone wrote (or forwarded, or linked) can go on; the hub checks who may read it and where it may go. */
  canForward(m: ThreadMessage): boolean {
    return m.kind !== 'system' && !m.pending && !m.deletedAt;
  }

  /** "Forward to…" (a panel in the window), then the copies, each carrying where it came from. */
  forward(m: ThreadMessage): void {
    const content = copyContent(m);
    const preview = content?.kind === 'text' ? content.text : m.attachments?.map((a) => a.fileName).join(', ') || 'A message';
    const data: ForwardPickerData = { sourceConversationId: this.conversation.id, preview };
    this.sheets.open<string[]>(ForwardPickerComponent, { data }).afterClosed().subscribe((ids) => {
      if (!ids?.length) return;
      this.chat.store.forward(m.id, ids).then(
        () => {
          const ref = this.snack.open(ids.length === 1 ? 'Forwarded' : `Forwarded to ${ids.length} conversations`, ids.length === 1 ? 'Open' : undefined, { duration: 4000 });
          if (ids.length === 1) ref.onAction().subscribe(() => this.chat.openConversation(ids[0]));
        },
        (e: unknown) => this.fail(e),
      );
    });
  }

  /** A forwarded copy's way back: the original conversation, at the message (when this reader may open it). */
  async openForwardedOriginal(from: ForwardedFrom): Promise<void> {
    if (!from.conversationId) return;
    this.scrolledToSeq = null;
    try {
      await this.chat.openConversationById(from.conversationId, from.seq ?? undefined);
    } catch {
      this.snack.open('That conversation is not one you can open.', 'OK', { duration: 4000 });
    }
  }

  // ── filing to patients ──────────────────────────────────────────────────────────

  get filingText(): string {
    return filingLabel(this.filing);
  }

  /** Some message in view is filed to patients: the thread leaves room on the left for the filing line. */
  hasFilingLine(rows: Row[]): boolean {
    return rows.some((r) => r.kind === 'filing-open' || (r.kind === 'message' && !!r.filing));
  }

  private async loadFiling(): Promise<void> {
    const id = this.conversation.id;
    try {
      const filing = await this.chat.store.filing(id);
      if (this.conversation.id === id) this.filing = filing;
    } catch {
      // No banner rather than an error: the conversation still works.
    }
  }

  async endFiling(): Promise<void> {
    try {
      await this.chat.store.endFiling(this.conversation.id);
      this.filing = null;
    } catch (e) {
      this.fail(e);
    }
  }

  // ── pinned messages ──────────────────────────────────────────────────────────────

  private async loadPins(): Promise<void> {
    const id = this.conversation.id;
    try {
      const pins = await this.chat.store.pins(id);
      if (this.conversation.id !== id) return;
      this.pins = this.supportPins(pins);
      this.pinIndex = Math.min(this.pinIndex, Math.max(0, pins.length - 1));
    } catch {
      // No bar rather than an error: the conversation still works.
    }
  }

  canPin(m: ThreadMessage): boolean {
    return this.writable() && m.kind !== 'system' && !m.pending && !m.deletedAt;
  }

  isPinned(m: ThreadMessage): boolean {
    return this.pins.some((p) => p.messageId === m.id);
  }

  async togglePin(m: ThreadMessage): Promise<void> {
    try {
      this.pins = this.supportPins(this.isPinned(m) ? await this.chat.store.unpin(m.id) : await this.chat.store.pin(m.id));
      this.pinIndex = 0;
    } catch (e) {
      if (e instanceof HubRequestError && e.code === 'PIN_LIMIT') {
        this.snack.open('Up to three messages can be pinned here. Unpin one first.', 'OK', { duration: 5000 });
      } else {
        this.fail(e);
      }
    }
  }

  /** The pinned bar: go to the message it shows, and show the next pin for the next tap (as WhatsApp's). */
  async goToPin(): Promise<void> {
    const pin = this.pins[this.pinIndex];
    if (!pin) return;
    // The same pin again after scrolling away: scroll to it again.
    this.scrolledToSeq = null;
    this.pinIndex = (this.pinIndex + 1) % this.pins.length;
    try {
      await this.chat.openConversationById(this.conversation.id, pin.seq);
    } catch (e) {
      this.fail(e);
    }
  }

  async unpinShown(e: Event): Promise<void> {
    e.stopPropagation();
    const pin = this.pins[this.pinIndex];
    if (!pin) return;
    try {
      this.pins = this.supportPins(await this.chat.store.unpin(pin.messageId));
      this.pinIndex = 0;
    } catch (err) {
      this.fail(err);
    }
  }

  /** Where a linked copy came from: a billing chat by the side it is with, never as "a direct message". */
  linkedFromLabel(from: LinkedFrom): string {
    return linkedFromLabel(from, from.conversationId ? this.chat.store.snapshot.conversations[from.conversationId.toLowerCase()] : undefined);
  }

  /**
   * "Go to the original": the conversation this copy was linked FROM, opened at the message itself (§8.4).
   *
   * A copy is frozen, so reading it tells you what was said but not what was being discussed. This is the way back.
   * It can fail honestly — the original may live in a channel or a direct message this reader is not part of, which
   * is exactly why the label says only '#channel' or 'a direct message'.
   */
  async openOriginal(from: LinkedFrom): Promise<void> {
    if (!from.conversationId) return;
    this.scrolledToSeq = null; // a DM the reader is not in: the hub did not say which (no button either)
    try {
      await this.chat.openConversationById(from.conversationId, from.seq ?? undefined);
    } catch {
      this.snack.open('That conversation is not one you can open.', 'OK', { duration: 4000 });
    }
  }

  /** The first link in this browser explains what linking does (§8.3); after that it just links. */
  private async confirmLinking(names: string[]): Promise<boolean> {
    try {
      if (localStorage.getItem(LINK_WARNING_KEY)) return true;
    } catch {
      // storage blocked: ask every time
    }
    const who = names.length === 1 ? `${names[0]}'s` : `each patient's (${names.join(', ')})`;
    const ok = await this.sheets.confirm({
      title: 'Link to the patient record',
      message: `Linking copies this message, and every message after it until you end filing, into ${who} patient chat. Everyone in the practice who can see the patient can read them there, and they show on the patient's timeline. The copies stay as they are, even if a message is later edited or deleted.`,
      confirmText: 'Link',
    });
    if (ok) {
      try {
        localStorage.setItem(LINK_WARNING_KEY, '1');
      } catch {
        // fine: asked again next time
      }
    }
    return !!ok;
  }

  startEdit(m: ThreadMessage): void {
    this.editing = { id: m.id, text: m.body ?? '' };
    this.draft = m.body ?? '';
    this.replyTo = null;
    setTimeout(() => this.composer?.nativeElement.focus());
  }

  startReply(m: ThreadMessage): void {
    this.replyTo = m;
    this.editing = null;
    setTimeout(() => this.composer?.nativeElement.focus());
  }

  cancelCompose(): void {
    if (this.editing) this.draft = '';
    this.editing = null;
    this.replyTo = null;
    this.linking = null;
  }

  // ── message actions ──────────────────────────────────────────────────────────────

  openActions(m: ThreadMessage, e?: MouseEvent): void {
    e?.preventDefault();
    this.actionsFor = m.id;
    // A card low in the thread opens its menu upwards, so the thread's edge does not cut it off.
    const el = this.scroller?.nativeElement;
    const row = m.card ? el?.querySelector<HTMLElement>(`.msg[data-seq="${m.seq}"]`) : null;
    if (el && row) {
      const box = el.getBoundingClientRect();
      const r = row.getBoundingClientRect();
      this.cardMenuUp = r.top + r.height / 2 > box.top + box.height / 2;
    }
  }

  /** The open card menu opens upwards. */
  cardMenuUp = false;

  /** Any click (an action in the bar included) or Escape closes the bar. */
  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  closeActions(): void {
    this.actionsFor = null;
  }

  toggleReaction(m: ThreadMessage, emoji: string, me: string | null): void {
    if (this.conversation.access !== 'participant') return;
    this.chat.store.react(m.id, emoji, !this.reacted(m, emoji, me)).catch((e: unknown) => this.fail(e));
  }

  async remove(m: ThreadMessage): Promise<void> {
    const ok = await this.sheets.confirm({ title: 'Delete message', message: 'Delete this message for everyone in the conversation?', confirmText: 'Delete', danger: true });
    if (!ok) return;
    this.chat.store.remove(m.id).catch((e: unknown) => this.fail(e));
  }

  /** An unlinked copy says who took it off this patient's chat, in place of "This message was deleted". */
  unlinkedLabel(from: LinkedFrom | undefined): string {
    return unlinkedLabel(from);
  }

  unlinkTargets(m: ThreadMessage): UnlinkTarget[] {
    return unlinkTargets(m, this.visibleLinks(m), this.conversation, this.title());
  }

  /**
   * Unlinks the run under a patient heading: this message and the later ones filed with it, from the patients the
   * heading names (asked which, when there are several).
   */
  async unlinkRun(m: ThreadMessage): Promise<void> {
    const links = this.visibleLinks(m);
    if (!links.length) return;
    try {
      const scopes = await Promise.all(links.map((l) => this.chat.store.unlinkScope(m.id, l.conversationId)));
      const patients = links.map((l, i) => ({ name: l.name || 'this patient', count: scopes[i].later + 1, filing: scopes[i].filing }));
      const q = unlinkRunQuestion(patients);
      const chosen = q.choices ? await this.sheets.pick(q) : (await this.sheets.confirm(q)) ? [0] : null;
      if (!chosen?.length) return;
      let unlinked = 0;
      for (const i of chosen) unlinked += (await this.chat.store.unlink(m.id, links[i].conversationId, true)).unlinked;
      this.snack.open(unlinkRunDone(unlinked, chosen.map((i) => patients[i].name)), 'OK', { duration: 3000 });
    } catch (e) {
      this.fail(e);
    }
  }

  /** The list is rebuilt on every check: without this the button is replaced mid-click and the click is lost. */
  trackUnlink(_i: number, t: UnlinkTarget): string {
    return t.conversationId;
  }

  /**
   * Takes a message filed to the wrong patient off their chat. When later messages were filed there with it, asks
   * whether to unlink those too. The hub sends the changed messages back, so the thread and filing line follow.
   */
  async unlink(t: UnlinkTarget): Promise<void> {
    try {
      const scope = await this.chat.store.unlinkScope(t.messageId, t.conversationId);
      const q = unlinkQuestion(t.patient, scope);
      // Cancel, then "Only this one", then "Unlink all N" when later messages went with it.
      const answer = q.all
        ? await this.sheets.ask({ title: q.title, message: q.message, secondText: q.one, confirmText: q.all, danger: true })
        : (await this.sheets.confirm({ title: q.title, message: q.message, confirmText: q.one, danger: true })) ? 'second' : null;
      if (!answer) return;
      await this.chat.store.unlink(t.messageId, t.conversationId, answer === 'confirm');
    } catch (e) {
      this.fail(e);
    }
  }

  retry(m: ThreadMessage): void {
    this.chat.store.retry(this.conversation.id, m.id).catch((e: unknown) => this.fail(e));
  }

  discard(m: ThreadMessage): void {
    this.chat.store.discardFailed(this.conversation.id, m.id);
  }

  join(): void {
    this.chat.store.join(this.conversation.id).catch((e: unknown) => this.fail(e));
  }

  openInfo(): void {
    this.sheets.open(ConversationInfoDialogComponent, { data: { conversationId: this.conversation.id, isOrgAdmin: this.isOrgAdmin } });
  }

  /** A log request from support, in the user's own support chat: they may answer it (the agent's side only sees it). */
  canAnswerLogs(m: ThreadMessage): boolean {
    return this.conversation.type === 'support' && this.conversation.support?.side === 'requester' && m.logRequest?.role === 'request';
  }

  /**
   * Answers support's log request. Sharing sends what log-capture.ts kept since the app started, as a text file, with
   * what support needs to read it (app version, platform, the page). The prompt changes to the answer on every device.
   */
  async answerLogs(m: ThreadMessage, accept: boolean): Promise<void> {
    if (this.answeringLogs.has(m.id)) return;
    this.answeringLogs.add(m.id);
    try {
      if (accept) {
        const text = logReport({ 'App version': environment.APP_VERSION, Platform: Capacitor.getPlatform(), Page: this.router.url });
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        await this.chat.store.answerLogRequest(m, { accept: true, logs: new Blob([text], { type: 'text/plain' }), fileName: `medicai-app-logs-${stamp}.txt` });
        this.snack.open('Your app logs were sent to MedicAI Support.', 'OK', { duration: 4000 });
      } else {
        await this.chat.store.answerLogRequest(m, { accept: false });
      }
    } catch (e) {
      if (e instanceof HubRequestError && e.code === 'CONFLICT') this.snack.open('This request has already been answered.', 'OK', { duration: 4000 });
      else this.fail(e);
    } finally {
      this.answeringLogs.delete(m.id);
    }
  }

  // ── network chat: referring a patient linked here (2026-09-22) ─────────────────────────

  /*
   * A patient linked in a 1-on-1 with someone outside the practice is SENT AS TYPED (owner, N2). Its sender then sees,
   * under the message and to them alone, whether that person has been referred the patient: if not, an offer to refer
   * (the patient referral dialog, pre-set to that network link, N3); once referred, "Referred to …" (N4). Nothing here
   * reaches the other side: the referral they receive is the ordinary one.
   */

  /** The referral network link on the other end of this network chat, or null anywhere else. */
  private get networkLink(): string | null {
    return this.conversation.type === 'network' ? this.conversation.network?.counterpart.linkRef ?? null : null;
  }

  /** Who is on the other end, as a sentence names them: "Dr Smith (Rosebank Practice)". */
  counterpartLabel(): string {
    const c = this.conversation.network?.counterpart;
    if (!c) return 'them';
    const name = c.name || c.practiceName || 'them';
    return c.practiceName && c.name ? `${name} (${c.practiceName})` : name;
  }

  /** Admin or Physician of this conversation's practice may refer (as the patient file); the server checks it again. */
  get canRefer(): boolean {
    const gid = this.chat.groupIdOfOrg(this.conversation.orgId);
    const group = this.groups.group;
    if (!gid || !group || String(group.id).toLowerCase() !== gid.toLowerCase()) return false;
    const role = String(group.userRole || '').toLowerCase();
    return role === 'admin' || role === 'physician';
  }

  /** The prompts still asking (or being checked, or linking) under one message. */
  promptsFor(messageId: string): NetworkReferralPrompt[] {
    return (this.chat.networkPrompts.get(messageId) ?? []).filter((p) => p.state !== 'referred' && p.state !== 'declined');
  }

  /*
   * The chips under a message in a network chat (owner, 2026-09-22). A patient linked here is copied into the practice's
   * own patient chat first; the other side sees nothing of it. Once the patient is referred over this chat's link, the
   * message is ALSO linked into the referral chat (msg:linkReferral), which both sides see. So:
   *   - a referral link shows as "<patient> · shared with <them>" and opens the patient's file;
   *   - the patient's own-practice link is then left out (it is the same patient, twice);
   *   - a patient link with no referral link says it is not shared, with a way to share it (refer first if need be).
   */

  /** The links to show under a message: in a network chat, one per patient, the shared one when there is one. */
  visibleLinks(m: ThreadMessage): LinkedTo[] {
    const links = m.linkedTo ?? [];
    if (this.conversation.type !== 'network') return links;
    const shared = new Set(links.filter((l) => l.kind === 'referral' && l.patientRef).map((l) => l.patientRef!.toLowerCase()));
    return links.filter((l) => l.kind === 'referral' || !l.patientRef || !shared.has(l.patientRef.toLowerCase()));
  }

  /** A link that stayed in this practice: a network chat's patient not (yet) shared with the other side, and not being asked about. */
  notShared(m: ThreadMessage, l: LinkedTo): boolean {
    if (this.conversation.type !== 'network' || l.kind === 'referral' || !l.patientRef || !this.networkLink) return false;
    const prompt = (this.chat.networkPrompts.get(m.id) ?? []).find((p) => p.patientRef === l.patientRef);
    return !prompt || prompt.state === 'declined';
  }

  /** A link chip: a referral link opens the patient's file (the chat is shared; the file is ours), others the patient chat. */
  openLink(l: LinkedTo): void {
    if (l.kind === 'referral') {
      if (l.patientRef) this.openPatientFile(l.patientRef);
      else this.openLinked(l.conversationId);
      return;
    }
    this.openLinked(l.conversationId);
  }

  /** "Share": checks again, links it if the patient is referred now, and otherwise offers the referral (the same prompt). */
  shareLink(m: ThreadMessage, l: LinkedTo): void {
    if (!l.patientRef) return;
    const left = (this.chat.networkPrompts.get(m.id) ?? []).filter((p) => p.patientRef !== l.patientRef);
    if (left.length) this.chat.networkPrompts.set(m.id, left);
    else this.chat.networkPrompts.delete(m.id);
    this.checkNetworkReferrals(m.id, [{ patientRef: l.patientRef, title: l.name || 'the patient' }]);
  }

  /** The patients from one message now referred to the other side: the attribution. */
  referredFor(messageId: string): NetworkReferralPrompt[] {
    return (this.chat.networkPrompts.get(messageId) ?? []).filter((p) => p.state === 'referred');
  }

  trackConnectPrompt(_i: number, p: ConnectPatientPrompt): string {
    return p.patientRef;
  }

  trackPrompt(_i: number, p: NetworkReferralPrompt): string {
    return p.patientRef;
  }

  // -- MedicAI Connect chats: a linked patient Connect does not have yet --------------------------------

  /** The practice's prompts, in a MedicAI Connect chat seen from the practice side; null anywhere else. */
  private get connectPrompts(): ConnectPatientPrompts | null {
    if (this.conversation.type !== 'billing' || this.conversation.billing?.side !== 'practice') return null;
    const groupId = this.chat.groupIdOfOrg(this.conversation.orgId);
    return groupId ? this.chat.connectPromptsOf(groupId) : null;
  }

  private checkConnectPatients(messageId: string, patients: readonly { patientRef: string; title: string }[]): void {
    this.connectPrompts?.check(messageId, patients);
  }

  connectPromptsFor(messageId: string): ConnectPatientPrompt[] {
    return this.connectPrompts?.of(messageId) ?? [];
  }

  sendToConnect(messageId: string, p: ConnectPatientPrompt): void {
    this.connectPrompts?.send(messageId, p.patientRef);
  }

  retryConnect(messageId: string, p: ConnectPatientPrompt): void {
    this.connectPrompts?.retry(messageId, p.patientRef);
  }

  dismissConnect(messageId: string, p: ConnectPatientPrompt): void {
    this.connectPrompts?.dismiss(messageId, p.patientRef);
  }

  /** Starts the check for each patient linked in one message (once per patient per message). */
  private checkNetworkReferrals(messageId: string, patients: readonly { patientRef: string; title: string }[]): void {
    const link = this.networkLink;
    const groupId = this.chat.groupIdOfOrg(this.conversation.orgId);
    if (!link || !groupId || !patients.length) return;
    const existing = this.chat.networkPrompts.get(messageId) ?? [];
    const fresh: NetworkReferralPrompt[] = patients
      .filter((p) => !existing.some((e) => e.patientRef === p.patientRef))
      .map((p) => ({ patientRef: p.patientRef, title: p.title, state: 'checking', since: null }));
    if (!fresh.length) return;
    this.chat.networkPrompts.set(messageId, [...existing, ...fresh]);
    for (const p of fresh) void this.recheckReferral(messageId, p.patientRef, groupId, link);
  }

  /**
   * Asks this app's server what the patient's referrals are and whether one reaches `link`, and links the message into
   * the patient's REFERRAL chat when it can (owner, 2026-09-22), so the other side sees the link too.
   *
   * A referral made a moment ago is not sharing yet: its recipient waits (`awaiting_link`) for a few hundred milliseconds
   * and its referral chat is made just after (seen 2026-09-22: created 11:33:03.98, sharing 11:33:04.16, chat 11:33:04.23).
   * So a check that finds it settling, or a link the hub refuses because the chat is not ready, is tried again on
   * NETWORK_SHARE_RETRY_MS (about 20 s). `sentNow`: the dialog has just reported a referral to that link, so a check that
   * does not see it yet is tried again too. When the retries run out the prompt goes and the chip says what is true:
   * "Share with ..." (one tap links it), "not joined yet", or "Not shared · Refer".
   */
  private async recheckReferral(messageId: string, patientRef: string, groupId: string, link: string, sentNow = false, attempt = 0): Promise<void> {
    const conversationId = this.conversation.id;
    const again = (): boolean => {
      if (attempt >= NETWORK_SHARE_RETRY_MS.length) return false;
      setTimeout(() => {
        if (this.destroyed || this.conversation.id !== conversationId) return;
        void this.recheckReferral(messageId, patientRef, groupId, link, sentNow, attempt + 1);
      }, NETWORK_SHARE_RETRY_MS[attempt]);
      return true;
    };
    let referrals;
    try {
      referrals = await firstValueFrom(this.referrals.listPatientReferrals(patientRef, groupId));
    } catch {
      if (sentNow && again()) return;
      this.setShareStatus(link, patientRef, 'error');
      this.setPrompt(messageId, patientRef, { state: 'error', since: null });
      return;
    }
    const decision = networkShareDecision(referrals, link);
    this.setShareStatus(link, patientRef, decision.kind);
    if (decision.kind === 'none') {
      if (sentNow && again()) return;
      forgetReferred(messageId, patientRef);
      if (sentNow) this.dropPrompt(messageId, patientRef);
      else this.setPrompt(messageId, patientRef, { state: 'ask', since: null });
      return;
    }
    if (decision.kind === 'awaiting') {
      // Referred to someone who has not joined: nothing to link into yet. The marker stays, so it completes once they have.
      this.setPrompt(messageId, patientRef, { state: 'referred', since: decision.since });
      return;
    }
    this.setPrompt(messageId, patientRef, { state: 'linking', since: decision.since });
    if (decision.kind === 'settling') {
      if (!again()) this.dropPrompt(messageId, patientRef);
      return;
    }
    try {
      await this.chat.linkReferral(messageId, decision.recipientId, groupId);
      forgetReferred(messageId, patientRef);
      this.dropPrompt(messageId, patientRef);
    } catch {
      if (!again()) this.dropPrompt(messageId, patientRef);
    }
  }

  /**
   * A message whose patient this user referred from the prompt (referFromChat) but that is not linked into the referral
   * chat yet, because the referral was still settling when they left or its clinician had not joined: completed when the
   * chat is opened again, without asking. Only those: the user chose to share them. Any other patient chip waits for a
   * tap on "Share with ...", since linking into the referral chat is what shows the message to the other side.
   */
  private resumeReferredLinks(): void {
    if (this.conversation.type !== 'network' || !this.networkLink) return;
    for (const [messageId, entries] of referredMarkers(this.conversation.id)) this.checkNetworkReferrals(messageId, entries);
  }

  /**
   * The chip under a patient link that stayed in this practice (network chat): 'share' when the patient is referred over
   * this chat's link and sharing, so one tap links it; 'awaiting' when referred but the clinician has not joined;
   * 'checking'; otherwise not shared, with Refer. Asks the server once per patient the first time the chip shows.
   */
  shareStateOf(l: LinkedTo): 'share' | 'awaiting' | 'checking' | 'not_shared' {
    const link = this.networkLink;
    const groupId = this.chat.groupIdOfOrg(this.conversation.orgId);
    if (!link || !groupId || !l.patientRef) return 'not_shared';
    const key = shareKey(link, l.patientRef);
    const known = this.shareStatus.get(key);
    if (known === undefined) {
      this.shareStatus.set(key, 'checking');
      firstValueFrom(this.referrals.listPatientReferrals(l.patientRef, groupId))
        .then((referrals) => this.shareStatus.set(key, networkShareDecision(referrals, link).kind))
        .catch(() => this.shareStatus.set(key, 'error'));
      return 'checking';
    }
    if (known === 'share' || known === 'settling') return 'share';
    if (known === 'awaiting' || known === 'checking') return known;
    return 'not_shared';
  }

  private setShareStatus(link: string, patientRef: string, status: NetworkShareDecision['kind'] | 'error'): void {
    this.shareStatus.set(shareKey(link, patientRef), status);
  }

  private dropPrompt(messageId: string, patientRef: string): void {
    const left = (this.chat.networkPrompts.get(messageId) ?? []).filter((x) => x.patientRef !== patientRef);
    if (left.length) this.chat.networkPrompts.set(messageId, left);
    else this.chat.networkPrompts.delete(messageId);
  }

  private setPrompt(messageId: string, patientRef: string, patch: Partial<NetworkReferralPrompt>): void {
    const list = this.chat.networkPrompts.get(messageId);
    if (!list) return;
    this.chat.networkPrompts.set(messageId, list.map((p) => (p.patientRef === patientRef ? { ...p, ...patch } : p)));
  }

  /** "Not now": the offer goes; the patient stays linked in the sender's own practice, its chip saying it is not shared. */
  notNow(messageId: string, p: NetworkReferralPrompt): void {
    this.setPrompt(messageId, p.patientRef, { state: 'declined' });
  }

  /** "Try again" after a check that failed. */
  retryPrompt(messageId: string, p: NetworkReferralPrompt): void {
    const link = this.networkLink;
    const groupId = this.chat.groupIdOfOrg(this.conversation.orgId);
    if (!link || !groupId) return;
    this.setPrompt(messageId, p.patientRef, { state: 'checking' });
    void this.recheckReferral(messageId, p.patientRef, groupId, link);
  }

  /**
   * "Send referral": the patient referral dialog, open on a new referral to this network link. The dialog belongs to
   * the (lazy) patient module, so it is loaded with that module and opened in its injector, exactly as the patient
   * file opens it. When it closes, the patient's referrals are read again: that, not the dialog, decides the line.
   */
  async referFromChat(messageId: string, p: NetworkReferralPrompt): Promise<void> {
    const link = this.networkLink;
    const groupId = this.chat.groupIdOfOrg(this.conversation.orgId);
    if (!link || !groupId || !this.canRefer) return;
    let ref: MatDialogRef<unknown, PatientReferralDialogResult>;
    try {
      const [{ PatientModule }, { PatientReferralDialogComponent }] = await Promise.all([
        import('../../../patient/patient.module'),
        import('../../../patient/components/patient-file/patient-referral-dialog/patient-referral-dialog.component'),
      ]);
      const moduleRef = createNgModule(PatientModule, this.injector);
      const data: PatientReferralDialogData = { patientId: p.patientRef, patientName: p.title, canRefer: true, preselectLinkId: link };
      ref = this.dialog.open(PatientReferralDialogComponent, { width: '720px', maxHeight: '90vh', data, injector: moduleRef.injector });
    } catch {
      this.snack.open('The referral could not be opened. Please try again.', 'OK', { duration: 4000 });
      return;
    }
    const result = await firstValueFrom(ref.afterClosed());
    const sent = !!result?.sentLinkIds?.some((id) => id.toLowerCase() === link.toLowerCase());
    if (sent) rememberReferred(this.conversation.id, messageId, p.patientRef, p.title);
    this.setPrompt(messageId, p.patientRef, { state: 'checking' });
    await this.recheckReferral(messageId, p.patientRef, groupId, link, sent);
  }

  private fail(e: unknown): void {
    const code = e instanceof HubRequestError ? e.code : 'INTERNAL';
    this.snack.open(ERROR_TEXT[code] ?? 'Something went wrong in chat.', 'OK', { duration: 4000 });
  }
}

/*
 * Messages whose patient was referred from a network chat's prompt and that wait to be linked into the referral chat
 * (2026-09-22): kept on the device, so the link completes after a reload or once the clinician joins. Storage can be
 * missing or blocked; then it completes only while the chat stays open, as before.
 */
const REFERRED_KEY = 'chat.networkReferred';
const REFERRED_TTL_MS = 30 * 24 * 60 * 60 * 1000;
type ReferredMarker = { conversationId: string; messageId: string; patientRef: string; title: string; at: number };

function shareKey(link: string, patientRef: string): string {
  return `${link}|${patientRef}`.toLowerCase();
}

function readReferred(): ReferredMarker[] {
  try {
    const list = JSON.parse(localStorage.getItem(REFERRED_KEY) || '[]') as ReferredMarker[];
    return Array.isArray(list) ? list.filter((m) => !!m && Date.now() - m.at < REFERRED_TTL_MS) : [];
  } catch {
    return [];
  }
}

function writeReferred(list: ReferredMarker[]): void {
  try {
    localStorage.setItem(REFERRED_KEY, JSON.stringify(list.slice(-200)));
  } catch {
    /* storage blocked: nothing to remember */
  }
}

function rememberReferred(conversationId: string, messageId: string, patientRef: string, title: string): void {
  const list = readReferred().filter((m) => !(m.messageId === messageId && m.patientRef === patientRef));
  writeReferred([...list, { conversationId, messageId, patientRef, title, at: Date.now() }]);
}

function forgetReferred(messageId: string, patientRef: string): void {
  const list = readReferred();
  const left = list.filter((m) => !(m.messageId === messageId && m.patientRef === patientRef));
  if (left.length !== list.length) writeReferred(left);
}

/** This conversation's markers, by message. */
function referredMarkers(conversationId: string): Map<string, { patientRef: string; title: string }[]> {
  const out = new Map<string, { patientRef: string; title: string }[]>();
  for (const m of readReferred()) {
    if (m.conversationId !== conversationId) continue;
    out.set(m.messageId, [...(out.get(m.messageId) ?? []), { patientRef: m.patientRef, title: m.title }]);
  }
  return out;
}

/** A picture as PNG, the one image type browsers accept on the clipboard. */
async function toPng(blob: Blob): Promise<Blob> {
  if (blob.type === 'image/png') return blob;
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
  return new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('png'))), 'image/png'));
}

/** Copies text through a hidden box, for a browser that refuses the clipboard API here. */
function copyTextTheOldWay(text: string): boolean {
  const box = document.createElement('textarea');
  box.value = text;
  box.setAttribute('readonly', '');
  box.style.position = 'fixed';
  box.style.opacity = '0';
  document.body.appendChild(box);
  box.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    box.remove();
  }
}
